import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { RichTextEditor } from '../components/ui/rich-text-editor';
import { Textarea } from '../components/ui/textarea';
import InstallPWA from '../components/InstallPWA';
import { authenticatedFetch } from '../utils/api';
import { renderFormattedLetter, convertLetterToFormattedHtml, convertMemoToFormattedHtml } from '../utils/letterFormatting';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '../components/ui/tabs';
import { toast } from 'sonner';  // Use Sonner toast instead of Shadcn
import PromotionPreparation from '../components/PromotionPreparation';
import { 
  FolderPlus, 
  Folder, 
  FolderOpen,
  Users, 
  BarChart3,
  Copy,
  Eye,
  EyeOff,
  FileText,
  Calendar,
  TrendingUp,
  School,
  Loader2,
  CheckCircle,
  XCircle,
  Plus,
  LogOut,
  Trash2,
  Download,
  BookOpen,
  ClipboardList,
  Save,
  Link2,
  Building2,
  UserPlus,
  Briefcase,
  ChevronDown,
  ChevronRight,
  Clock,
  MessageSquare,
  FileEdit,
  Sparkles,
  Printer,
  Send,
  AlertCircle,
  ThumbsUp,
  ThumbsDown,
  Forward,
  Bell,
  Upload,
  Stamp,
  CreditCard,
  ExternalLink,
  X,
  Paperclip,
  FileIcon,
  Plus as PlusIcon,
  GraduationCap
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Form validation schemas
const folderSchema = z.object({
  teacher_name: z.string().min(1, "Please enter teacher's name"),
  class_or_subject: z.string().min(1, "Please enter class or subject name")
});

const HeadteacherDashboard = () => {
  const { logout, user: authUser } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderContents, setFolderContents] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  // State for collapsible week folders in received lesson notes
  const [expandedWeeks, setExpandedWeeks] = useState({});
  
  // Offline/Cache state
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [dataFromCache, setDataFromCache] = useState(false);
  
  // Comment state
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [currentCommentSubmission, setCurrentCommentSubmission] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  
  // Term Report State
  const [teachersForReport, setTeachersForReport] = useState([]);
  const [reportData, setReportData] = useState({});
  const [termName, setTermName] = useState('Term 1');
  
  // Notification State (for lesson notes)
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  // User Notifications State (for payment links, SISO escalations)
  const [userNotifications, setUserNotifications] = useState([]);
  const [unreadUserNotificationCount, setUnreadUserNotificationCount] = useState(0);
  const [showUserNotificationPanel, setShowUserNotificationPanel] = useState(false);
  const [loadingUserNotifications, setLoadingUserNotifications] = useState(false);
  
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear() + '/' + (new Date().getFullYear() + 1));
  const [savingReport, setSavingReport] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [includeHeadteacher, setIncludeHeadteacher] = useState(false);
  
  // Report Type State (Start of Term / End of Term)
  const [reportType, setReportType] = useState('start_of_term');
  const [classAttendance, setClassAttendance] = useState({});
  const [headteacherRemarks, setHeadteacherRemarks] = useState('');
  
  // Teachers Work Output State
  const [teachersWorkOutput, setTeachersWorkOutput] = useState([]);
  const [workOutputVerifications, setWorkOutputVerifications] = useState({});
  const [savingVerification, setSavingVerification] = useState(false);
  const [loadingWorkOutput, setLoadingWorkOutput] = useState(false);
  
  // Weeks for work output (1-15)
  const workOutputWeeks = Array.from({ length: 15 }, (_, i) => i + 1);
  
  // Class list for attendance (will be populated from school data)
  const classOptions = [
    'KG 1', 'KG 2', 
    'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6',
    'JHS 1', 'JHS 2', 'JHS 3'
  ];
  
  // Teacher Rank and Portfolio Options
  const teacherRankOptions = ['Snr Sup II', 'Snr Sup I', 'PS', 'AD II', 'AD I', 'DD'];
  const teacherPortfolioOptions = [
    'Headteacher', 'Assistant Headteacher', 'Sports Secretary', 
    'Project Coordinator', 'Staff Secretary', 'Welfare Officer', 
    'SHEP Coordinator', 'Culture Coordinator', 'ICT Coordinator', 
    'PLC Lead', 'School Chaplain', 'Class Teacher', 'Subject Teacher'
  ];
  
  // SISO Connection State
  const [isConnectSISODialogOpen, setIsConnectSISODialogOpen] = useState(false);
  const [sisoConnectToken, setSisoConnectToken] = useState('');
  const [connectingSISO, setConnectingSISO] = useState(false);
  const [sisoConnection, setSisoConnection] = useState(null);
  
  // New lesson notes notification state
  const [lastViewedTimestamp, setLastViewedTimestamp] = useState(() => {
    const saved = localStorage.getItem('headteacher_notes_last_viewed');
    return saved ? new Date(saved) : null;
  });
  const [newNotesCount, setNewNotesCount] = useState(0);
  
  // Applications state
  const [applicationView, setApplicationView] = useState('teacher'); // 'teacher' or 'my'
  const [teacherApplications, setTeacherApplications] = useState([]);
  const [newTeacherAppsCount, setNewTeacherAppsCount] = useState(0);
  const [loadingTeacherApps, setLoadingTeacherApps] = useState(false);
  const [myApplications, setMyApplications] = useState([]);
  const [applicationPlainText, setApplicationPlainText] = useState('');
  const [applicationType, setApplicationType] = useState('');
  const [generatedLetter, setGeneratedLetter] = useState('');
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [showLetterPreview, setShowLetterPreview] = useState(false);
  const [processingAction, setProcessingAction] = useState(null);
  const [actionComment, setActionComment] = useState('');
  
  // Application Preview Modal State (for viewing teacher applications with actions)
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationPreview, setShowApplicationPreview] = useState(false);
  const [previewActionMode, setPreviewActionMode] = useState(null); // 'approve', 'disapprove', 'escalate'
  
  // Rich Text Editor state for application editing
  const [showRichEditor, setShowRichEditor] = useState(false);
  const [editedLetterHtml, setEditedLetterHtml] = useState('');
  
  // Application Attachments state (for headteacher's own applications)
  const [applicationAttachments, setApplicationAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = React.useRef(null);
  
  // Application draft state
  const [letterSaved, setLetterSaved] = useState(false);
  const [savingLetter, setSavingLetter] = useState(false);
  const [applicationDraftId, setApplicationDraftId] = useState(null);
  
  // Application types
  const applicationTypes = [
    { value: 'permission_for_absent', label: 'Permission for Absent' },
    { value: 'application_for_reposting', label: 'Application for Reposting' },
    { value: 'application_for_leave', label: 'Application for Leave' },
    { value: 'application_for_return_movement', label: 'Application for Return Movement' },
    { value: 'application_for_promotion', label: 'Application for Promotion' },
    { value: 'application_for_upgrading', label: 'Application for Upgrading' },
    { value: 'application_for_salary_adjustment', label: 'Application for Salary Adjustment' }
  ];
  
  // Utility function to format letter/memo for display (strips technical markers)
  const formatLetterForDisplay = (letter) => {
    if (!letter) return '';
    
    let formatted = letter
      .replace(/\[RIGHT\]\s*/g, '')
      .replace(/\[LEFT\]\s*/g, '')
      .replace(/\[CENTER\]\s*/g, '')
      .replace(/\[BOLD_UNDERLINE\]/g, '')
      .replace(/\[\/BOLD_UNDERLINE\]/g, '')
      .replace(/\[\/?BOLD_UNDERLINE\]/g, '')
      .replace(/\[SIGNATURE_PLACEHOLDER\]/g, '\n\n____________________\n(Signature)')
      .replace(/\[STAMP_PLACEHOLDER\]/g, '\n\n[Official Stamp]')
      .replace(/\[SIGNATURE\]/g, '\n\n____________________\n(Signature)')
      .replace(/\[STAMP\]/g, '\n\n[Official Stamp]')
      .replace(/```/g, '');
    
    return formatted.trim();
  };

  // Note: renderFormattedLetter and convertLetterToFormattedHtml are imported from ../utils/letterFormatting
  
  // State for disapprove comment flow
  const [disapprovingAppId, setDisapprovingAppId] = useState(null);
  const [disapproveComment, setDisapproveComment] = useState('');
  
  // State for escalation memo flow
  const [escalatingAppId, setEscalatingAppId] = useState(null);
  const [escalateMemoText, setEscalateMemoText] = useState('');
  const [formattedMemo, setFormattedMemo] = useState('');
  const [generatingMemo, setGeneratingMemo] = useState(false);
  const [showMemoPreview, setShowMemoPreview] = useState(false);
  const [editedMemoHtml, setEditedMemoHtml] = useState('');
  const [memoSaved, setMemoSaved] = useState(false);
  const [savingMemo, setSavingMemo] = useState(false);
  
  // Profile/Settings state
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [uploadingStamp, setUploadingStamp] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    town: '',
    phone: '',
    file_number: '',
    mobile_money_number: '',
    mobile_money_network: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // toast is imported from 'sonner' - no need to destructure

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if error occurs
      localStorage.removeItem('auth_token');
      window.location.href = '/';
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(folderSchema)
  });

  // Load dashboard data
  const loadDashboard = async () => {
    setLoading(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/dashboard`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
        setDataFromCache(false);
        setIsOffline(false);
      } else {
        throw new Error('Failed to load dashboard');
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setIsOffline(!navigator.onLine);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Load teachers
  const loadTeachers = async () => {
    // No longer needed - teachers are entered as text input
    return;
  };

  // Load weekly stats
  const loadWeeklyStats = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/stats/weekly`, {
        credentials: 'include'
      });

      if (response.ok) {
        const stats = await response.json();
        setWeeklyStats(stats);
      }
    } catch (error) {
      console.error('Error loading weekly stats:', error);
    }
  };

  // Load notifications
  const loadNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/notifications`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadNotificationCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      await authenticatedFetch(`${backendUrl}/api/headteacher/notifications/${notificationId}/read`, {
        method: 'PUT',
        credentials: 'include'
      });

      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? {...n, is_read: true} : n)
      );
      setUnreadNotificationCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsRead = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      await authenticatedFetch(`${backendUrl}/api/headteacher/notifications/mark-all-read`, {
        method: 'PUT',
        credentials: 'include'
      });

      setNotifications(prev => prev.map(n => ({...n, is_read: true})));
      setUnreadNotificationCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications read:', error);
      toast.error('Failed to mark notifications as read');
    }
  };

  // Load user notifications (for payment links from SISO escalations)
  const loadUserNotifications = async () => {
    setLoadingUserNotifications(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/user/notifications`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserNotifications(data.notifications || []);
        setUnreadUserNotificationCount(data.notifications?.filter(n => !n.read)?.length || 0);
      }
    } catch (error) {
      console.error('Error loading user notifications:', error);
    } finally {
      setLoadingUserNotifications(false);
    }
  };

  // Mark user notification as read
  const markUserNotificationRead = async (notificationId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await authenticatedFetch(`${backendUrl}/api/user/notifications/${notificationId}/read`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      setUserNotifications(prev => 
        prev.map(n => n.id === notificationId ? {...n, read: true} : n)
      );
      setUnreadUserNotificationCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking user notification read:', error);
    }
  };

  // Handle payment link click
  const handlePaymentClick = (notification) => {
    if (notification.action_url) {
      // Mark as read first
      if (!notification.read) {
        markUserNotificationRead(notification.id);
      }
      // Open payment link in new tab
      window.open(notification.action_url, '_blank');
    }
  };

  // Create folder
  const onCreateFolder = async (data) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const newFolder = await response.json();
        
        toast.success("Folder created successfully with connect token");

        setIsCreateDialogOpen(false);
        reset();
        loadDashboard(); // Refresh dashboard
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Create folder failed:', response.status, errorData);
        
        if (response.status === 401) {
          toast.error("Session expired. Please login again.");
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else {
          toast.error(errorData.detail || 'Failed to create folder');
        }
        throw new Error(errorData.detail || 'Failed to create folder');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      if (error.message !== 'Failed to create folder') {
        toast.error(`Error: ${error.message}`);
      }
    }
  };

  // Toggle folder token
  const toggleFolderToken = async (folderId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/folders/${folderId}/token`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Token status updated successfully");
        loadDashboard(); // Refresh dashboard
      } else {
        throw new Error('Failed to toggle token');
      }
    } catch (error) {
      console.error('Error toggling token:', error);
      toast.error("Failed to update token status");
    }
  };

  // Load folder contents
  const loadFolderContents = async (folderId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/folders/${folderId}/contents`, {
        credentials: 'include'
      });

      if (response.ok) {
        const contents = await response.json();
        setFolderContents(contents);
      }
    } catch (error) {
      console.error('Error loading folder contents:', error);
    }
  };

  // Copy token to clipboard
  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    toast.success("Connect token copied to clipboard");
  };
  
  // Download submission - Mobile Safe Version using token-based URL
  const downloadSubmission = async (submissionId, fileName) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      toast.info(`📥 Preparing download: ${fileName}`, { duration: 2000 });
      
      // Create a temporary download token
      const tokenResponse = await authenticatedFetch(`${backendUrl}/api/headteacher/create-submission-token/${submissionId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!tokenResponse.ok) {
        throw new Error('Failed to prepare download');
      }
      
      const { token } = await tokenResponse.json();
      const downloadUrl = `${backendUrl}/api/public/headteacher-submission/${token}?mode=download`;
      
      // Use real <a> tag click to ensure mobile browsers handle it properly
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = fileName; // Suggest filename for download
      
      // Append, click, remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`✅ Downloading ${fileName}`);
    } catch (error) {
      console.error('Error downloading submission:', error);
      toast.error("Failed to download submission");
    }
  };

  // Read submission - Mobile Safe Version using token-based URL
  const readSubmission = async (submissionId, fileName) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      toast.info(`📄 Loading ${fileName}...`, { duration: 2000 });
      
      // Create a temporary view token
      const tokenResponse = await authenticatedFetch(`${backendUrl}/api/headteacher/create-submission-token/${submissionId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!tokenResponse.ok) {
        throw new Error('Failed to prepare document view');
      }
      
      const { token } = await tokenResponse.json();
      const viewUrl = `${backendUrl}/api/public/headteacher-submission/${token}?mode=view`;
      
      // Use real <a> tag click to ensure mobile browsers handle it properly
      const link = document.createElement('a');
      link.href = viewUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Append, click, remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`📄 Opening ${fileName}`);
    } catch (error) {
      console.error('Error opening submission:', error);
      toast.error("Failed to open submission");
    }
  };

  // Vet submission - Mark as vetted
  const vetSubmission = async (submissionId, isCurrentlyVetted) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/vet-submission/${submissionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ vetted: !isCurrentlyVetted })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update vetting status');
      }
      
      // Reload weekly stats to reflect changes
      await loadWeeklyStats();
      
      toast.success(!isCurrentlyVetted ? 'Lesson note vetted successfully' : 'Vetting removed');
    } catch (error) {
      console.error('Error vetting submission:', error);
      toast.error("Failed to update vetting status");
    }
  };

  // Open comment dialog for a submission
  const openCommentDialog = (submission) => {
    setCurrentCommentSubmission(submission);
    setCommentText(submission.headteacher_comment || '');
    setCommentDialogOpen(true);
  };

  // Save comment on submission
  const saveComment = async () => {
    if (!currentCommentSubmission) return;
    
    setSavingComment(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/comment-submission/${currentCommentSubmission.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ comment: commentText })
      });

      if (response.ok) {
        toast.success('Comment saved successfully');
        setCommentDialogOpen(false);
        
        // Reload weekly stats to reflect changes
        await loadWeeklyStats();
      } else {
        throw new Error('Failed to save comment');
      }
    } catch (error) {
      console.error('Error saving comment:', error);
      toast.error("Failed to save comment");
    } finally {
      setSavingComment(false);
    }
  };
  
  // Delete folder
  const deleteFolder = async (folderId, teacherName) => {
    if (!window.confirm(`Are you sure you want to delete the folder for ${teacherName}? This will also delete all associated submissions.`)) {
      return;
    }
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/folders/${folderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Folder deleted successfully. ${result.submissions_deleted} submissions removed.`);
        loadDashboard(); // Refresh dashboard
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete folder');
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error(`Failed to delete folder: ${error.message}`);
    }
  };

  // Toggle folder expansion
  const toggleFolderExpansion = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
    
    if (!expandedFolders[folderId]) {
      loadFolderContents(folderId);
    }
  };

  // Toggle week folder expand/collapse for received lesson notes
  const toggleWeekFolder = (teacherName, week) => {
    const key = `${teacherName}-${week}`;
    setExpandedWeeks(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Check if a week folder is expanded
  const isWeekExpanded = (teacherName, week) => {
    const key = `${teacherName}-${week}`;
    return expandedWeeks[key] || false;
  };

  // Get education level display name
  const getEducationLevelName = (level) => {
    switch (level) {
      case 'kg': return 'Kindergarten (KG)';
      case 'primary': return 'Primary School';
      case 'jhs': return 'Junior High School (JHS)';
      default: return level;
    }
  };

  // Load teachers for report
  const loadTeachersForReport = async () => {
    setLoadingTeachers(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/teachers-for-report`, {
        credentials: 'include'
      });

      if (response.ok) {
        const teachers = await response.json();
        setTeachersForReport(teachers);
        
        // Initialize report data for each teacher
        const initialData = {};
        teachers.forEach(teacher => {
          initialData[teacher.teacher_id] = {
            teacher_id: teacher.teacher_id,
            teacher_name: teacher.teacher_name,
            class_or_subject: teacher.class_or_subject,
            teacher_rank: '',
            teacher_portfolio: '',
            // Start of term fields
            is_present: true, // Default to present
            // End of term fields
            absent_with_permission: '',
            absent_without_permission: '',
            total_attendance: '',
            lesson_notes_received: 'None'
          };
        });
        setReportData(initialData);
        
        // Initialize class attendance
        const initialClassAttendance = {};
        classOptions.forEach(cls => {
          initialClassAttendance[cls] = '';
        });
        setClassAttendance(initialClassAttendance);
      } else {
        throw new Error('Failed to load teachers');
      }
    } catch (error) {
      console.error('Error loading teachers for report:', error);
      toast.error("Failed to load teachers for report");
    } finally {
      setLoadingTeachers(false);
    }
  };

  // Update report data for a teacher
  const updateReportField = (teacherId, field, value) => {
    setReportData(prev => ({
      ...prev,
      [teacherId]: {
        ...prev[teacherId],
        [field]: value
      }
    }));
  };
  
  // Update class attendance
  const updateClassAttendance = (className, value) => {
    setClassAttendance(prev => ({
      ...prev,
      [className]: value
    }));
  };

  // Save term report
  const saveTermReport = async () => {
    setSavingReport(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Prepare report data based on report type
      const reports = Object.values(reportData).map(report => ({
        ...report,
        // Convert empty strings to 0 for numeric fields
        absent_with_permission: parseInt(report.absent_with_permission) || 0,
        absent_without_permission: parseInt(report.absent_without_permission) || 0,
        total_attendance: parseInt(report.total_attendance) || 0
      }));
      
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/term-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          term_name: termName,
          academic_year: academicYear,
          report_type: reportType,
          reports: reports,
          class_attendance: classAttendance,
          headteacher_remarks: headteacherRemarks
        })
      });

      if (response.ok) {
        toast.success(`${reportType === 'start_of_term' ? 'Start of Term' : 'End of Term'} report saved successfully!`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save report');
      }
    } catch (error) {
      console.error('Error saving term report:', error);
      toast.error(`Failed to save report: ${error.message}`);
    } finally {
      setSavingReport(false);
    }
  };

  // Check SISO connection status
  const checkSISOConnection = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/siso-connection`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSisoConnection(data);
      }
    } catch (error) {
      console.error('Error checking SISO connection:', error);
    }
  };

  // Connect to SISO
  const connectToSISO = async () => {
    if (!sisoConnectToken.trim()) {
      toast.error("Please enter the connect token");
      return;
    }

    setConnectingSISO(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/connect-to-siso`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ connect_token: sisoConnectToken.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Successfully connected to SISO!");
        setSisoConnectToken('');
        setIsConnectSISODialogOpen(false);
        checkSISOConnection();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to connect');
      }
    } catch (error) {
      console.error('Error connecting to SISO:', error);
      toast.error(error.message || "Failed to connect to SISO");
    } finally {
      setConnectingSISO(false);
    }
  };

  // Load teacher applications submitted to this headteacher
  const loadTeacherApplications = async () => {
    setLoadingTeacherApps(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/teacher-applications`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTeacherApplications(data.applications || []);
        setNewTeacherAppsCount(data.new_count || 0);
      }
    } catch (error) {
      console.error('Error loading teacher applications:', error);
    } finally {
      setLoadingTeacherApps(false);
    }
  };

  // Process application action (approve/disapprove/escalate)
  const processApplicationAction = async (applicationId, action, comment = '', memo = '') => {
    setProcessingAction(applicationId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/application-action/${applicationId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: action,
          comment: comment,
          formatted_memo: memo
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || `Application ${action}d successfully!`);
        // Reset states
        setActionComment('');
        setDisapprovingAppId(null);
        setDisapproveComment('');
        setEscalatingAppId(null);
        setEscalateMemoText('');
        setFormattedMemo('');
        setShowMemoPreview(false);
        await loadTeacherApplications();
      } else {
        const error = await response.json();
        throw new Error(error.detail || `Failed to ${action} application`);
      }
    } catch (error) {
      console.error(`Error ${action}ing application:`, error);
      toast.error(error.message || `Failed to ${action} application`);
    } finally {
      setProcessingAction(null);
    }
  };

  // Handle disapprove click - show comment field first
  const handleDisapproveClick = (applicationId) => {
    if (disapprovingAppId === applicationId) {
      // Already showing comment field, now submit
      processApplicationAction(applicationId, 'disapprove', disapproveComment);
    } else {
      // Show comment field
      setDisapprovingAppId(applicationId);
      setDisapproveComment('');
    }
  };

  // Cancel disapprove
  const cancelDisapprove = () => {
    setDisapprovingAppId(null);
    setDisapproveComment('');
  };

  // Handle escalate click - show memo composition
  const handleEscalateClick = (applicationId) => {
    setEscalatingAppId(applicationId);
    setEscalateMemoText('');
    setFormattedMemo('');
    setShowMemoPreview(false);
  };

  // Cancel escalation
  const cancelEscalation = () => {
    setEscalatingAppId(null);
    setEscalateMemoText('');
    setFormattedMemo('');
    setShowMemoPreview(false);
    setEditedMemoHtml('');
    setMemoSaved(false);
  };

  // Generate AI-formatted escalation memo
  const generateEscalationMemo = async (teacherName, applicationType) => {
    if (!escalateMemoText.trim()) {
      toast.error('Please enter your memo content');
      return;
    }
    
    setGeneratingMemo(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/generate-escalation-memo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plain_text: escalateMemoText,
          teacher_name: teacherName,
          application_type: applicationType
        })
      });

      if (response.ok) {
        const data = await response.json();
        setFormattedMemo(data.formatted_memo);
        // Convert structured text to HTML for Tiptap editor
        const htmlContent = convertStructuredTextToHtml(data.formatted_memo);
        setEditedMemoHtml(htmlContent);
        setShowMemoPreview(true);
        setMemoSaved(false);
        toast.success('Memo generated — edit below, then save.');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate memo');
      }
    } catch (error) {
      console.error('Error generating memo:', error);
      toast.error(error.message || 'Failed to generate memo. Please try again.');
    } finally {
      setGeneratingMemo(false);
    }
  };

  // Submit escalation with memo
  const submitEscalation = async (applicationId) => {
    if (!formattedMemo) {
      toast.error('Please generate the memo first');
      return;
    }
    
    await processApplicationAction(applicationId, 'escalate', escalateMemoText, formattedMemo);
    cancelEscalation();
  };

  // Generate combined PDF (memo + teacher application)
  const generateEscalationPDF = async (applicationId) => {
    if (!formattedMemo) {
      toast.error('Please generate the memo first');
      return;
    }
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/generate-escalation-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          application_id: applicationId,
          escalation_memo: formattedMemo
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `escalation_${applicationId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('PDF generated successfully!');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(error.message || 'Failed to generate PDF');
    }
  };

  // Print memo as PDF
  const printMemo = () => {
    if (!formattedMemo) {
      toast.error('No memo content available to print');
      return;
    }
    
    // Pass both signature and stamp for the memo print
    const formattedHtml = convertMemoToFormattedHtml(formattedMemo, userProfile?.digital_signature, userProfile?.digital_stamp);
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Escalation Memo</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Times New Roman', Times, serif;
            font-size: 12pt;
            line-height: 1.3;
            max-width: 8.5in;
            margin: 0 auto;
            padding: 0.5in 0.75in;
          }
          .letter-content {
            word-wrap: break-word;
          }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .text-left { text-align: left; }
          .line { margin: 0; padding: 0; }
          .section-break { height: 0.8em; }
          .para-break { height: 0.5em; }
          .heading { 
            font-weight: bold; 
            text-decoration: underline;
            text-transform: uppercase;
            text-align: center;
            margin: 0.5em 0;
          }
          .bold { font-weight: bold; }
          .underline { text-decoration: underline; }
          .signature-img {
            max-height: 50px;
            margin: 3px 0;
          }
          .signature-line {
            border-bottom: 1px solid #000;
            width: 180px;
            margin-left: auto;
            margin-bottom: 2px;
          }
          @media print {
            body { 
              margin: 0; 
              padding: 0.4in 0.6in;
            }
            @page {
              margin: 0.4in;
              size: A4;
            }
          }
        </style>
      </head>
      <body>
        <div class="letter-content">
          ${formattedHtml}
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Convert structured text (with tags like [CENTER], [BOLD]) to HTML
  const convertStructuredTextToHtml = (text) => {
    if (!text) return '';
    
    // First, process multi-line formatting tags on the FULL text (before splitting)
    let fullText = text
      .replace(/\[BOLD_UNDERLINE\]([\s\S]*?)\[\/BOLD_UNDERLINE\]/g, '<strong><u>$1</u></strong>')
      .replace(/\[CENTER\]([\s\S]*?)\[\/CENTER\]/g, '<center_block>$1</center_block>')
      .replace(/\[RIGHT_ALIGN\]([\s\S]*?)\[\/RIGHT_ALIGN\]/g, '<right_block>$1</right_block>')
      .replace(/\[RIGHT\]([\s\S]*?)\[\/RIGHT\]/g, '<right_block>$1</right_block>')
      .replace(/\[SIGNATURE_PLACEHOLDER\]/gi, '<right_block><em>(Signature)</em></right_block>')
      .replace(/\[SIGNATURE\]/gi, '<right_block><em>(Signature)</em></right_block>')
      .replace(/\[STAMP_PLACEHOLDER\]/gi, '<right_block><em>(Stamp)</em></right_block>')
      .replace(/\[STAMP\]/gi, '<right_block><em>(Stamp)</em></right_block>')
      .replace(/\[\/?BOLD_UNDERLINE\]/g, '')
      .replace(/\[\/?RIGHT\]/g, '')
      .replace(/\[\/?RIGHT_ALIGN\]/g, '')
      .replace(/\[\/?CENTER\]/g, '')
      .replace(/\[\/?BOLD\]/g, '')
      .replace(/\[\/?UNDERLINE\]/g, '');

    let html = fullText
      .split('\n\n')
      .map(para => {
        let processed = para
          .replace(/\[BOLD\](.*?)\[\/BOLD\]/g, '<strong>$1</strong>')
          .replace(/\[UNDERLINE\](.*?)\[\/UNDERLINE\]/g, '<u>$1</u>')
          .replace(/<center_block>([\s\S]*?)<\/center_block>/g, '<p style="text-align: center;">$1</p>')
          .replace(/<right_block>([\s\S]*?)<\/right_block>/g, '<p style="text-align: right;">$1</p>');
        
        if (!processed.startsWith('<p')) {
          processed = `<p>${processed}</p>`;
        }
        return processed;
      })
      .join('');
    
    html = html.replace(/\n/g, '<br/>');
    return html;
  };

  // Convert HTML back to structured text for PDF generation
  const convertHtmlToStructuredText = (html) => {
    if (!html) return '';
    
    const div = document.createElement('div');
    div.innerHTML = html;
    
    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }
      
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName.toLowerCase();
        let content = Array.from(node.childNodes).map(processNode).join('');
        
        const style = node.getAttribute('style') || '';
        if (style.includes('text-align: center')) {
          content = `[CENTER]${content}[/CENTER]`;
        } else if (style.includes('text-align: right')) {
          content = `[RIGHT_ALIGN]${content}[/RIGHT_ALIGN]`;
        }
        
        if (tag === 'strong' || tag === 'b') {
          content = `[BOLD]${content}[/BOLD]`;
        }
        if (tag === 'u') {
          content = `[UNDERLINE]${content}[/UNDERLINE]`;
        }
        if (tag === 'p') {
          content = content + '\n\n';
        }
        if (tag === 'br') {
          content = '\n';
        }
        
        return content;
      }
      
      return '';
    };
    
    let text = Array.from(div.childNodes).map(processNode).join('');
    text = text.replace(/\n{3,}/g, '\n\n').trim();
    
    return text;
  };

  // Open rich text editor for editing
  const openRichEditor = () => {
    setShowRichEditor(true);
  };

  // Close rich text editor
  const closeRichEditor = () => {
    setShowRichEditor(false);
  };

  // Done editing - convert back to structured text and prepare for submission
  const doneEditingLetter = () => {
    const structuredText = convertHtmlToStructuredText(editedLetterHtml);
    setGeneratedLetter(structuredText);
    setShowRichEditor(false);
    toast.success('Letter updated! Ready to submit.');
  };

  // Generate AI-formatted application letter for headteacher's own application
  const generateApplicationLetter = async () => {
    if (!applicationType) {
      toast.error('Please select an application type');
      return;
    }
    if (!applicationPlainText.trim()) {
      toast.error('Please enter your application content');
      return;
    }
    
    setGeneratingLetter(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/generate-application-letter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          application_type: applicationTypes.find(t => t.value === applicationType)?.label || applicationType,
          plain_text: applicationPlainText
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedLetter(data.formatted_letter);
        // Convert structured text to HTML for rich text editor
        const htmlContent = convertStructuredTextToHtml(data.formatted_letter);
        setEditedLetterHtml(htmlContent);
        setShowLetterPreview(true);
        toast.success('Letter generated — edit below, then save.');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate letter');
      }
    } catch (error) {
      console.error('Error generating letter:', error);
      toast.error(error.message || 'Failed to generate letter. Please try again.');
    } finally {
      setGeneratingLetter(false);
    }
  };

  // Handle attachment upload (for headteacher's own applications)
  const handleAttachmentUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingAttachment(true);
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    for (const file of files) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 5MB`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await authenticatedFetch(`${backendUrl}/api/application/upload-attachment`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          setApplicationAttachments(prev => [...prev, {
            id: data.attachment_id,
            filename: data.filename,
            file_size: data.file_size,
            content_type: data.content_type
          }]);
          toast.success(`Attached: ${file.name}`);
        } else {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to upload attachment');
        }
      } catch (error) {
        console.error('Error uploading attachment:', error);
        toast.error(`Failed to attach ${file.name}: ${error.message}`);
      }
    }
    
    setUploadingAttachment(false);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  // Remove attachment
  const removeAttachment = async (attachmentId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/application/attachment/${attachmentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setApplicationAttachments(prev => prev.filter(a => a.id !== attachmentId));
        toast.success('Attachment removed');
      }
    } catch (error) {
      console.error('Error removing attachment:', error);
      toast.error('Failed to remove attachment');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Submit headteacher's own application to SISO
  const submitMyApplication = async () => {
    if (!applicationDraftId) {
      toast.error('Please save the letter first');
      return;
    }
    
    setSubmittingApplication(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/submit-application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          draft_id: applicationDraftId,
          attachment_ids: applicationAttachments.map(a => a.id)
        })
      });

      if (response.ok) {
        toast.success('Application submitted to SISO successfully!');
        setApplicationPlainText('');
        setApplicationType('');
        setGeneratedLetter('');
        setEditedLetterHtml('');
        setShowLetterPreview(false);
        setShowRichEditor(false);
        setLetterSaved(false);
        setApplicationDraftId(null);
        setApplicationAttachments([]);
        loadMyApplications();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmittingApplication(false);
    }
  };

  // Load headteacher's own applications
  const loadMyApplications = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/my-applications`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMyApplications(data.applications || []);
      }
    } catch (error) {
      console.error('Error loading my applications:', error);
    }
  };

  // Print/Download letter as PDF
  const printLetter = () => {
    const htmlToPrint = editedLetterHtml || '';
    if (!htmlToPrint && !generatedLetter) {
      toast.error('No letter content available to print');
      return;
    }
    
    let printContent = htmlToPrint;
    if (!printContent) {
      printContent = convertStructuredTextToHtml(generatedLetter);
    }
    
    // Embed signature if not already embedded
    const signatureUrl = userProfile?.digital_signature;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (signatureUrl && !printContent.includes('alt="Signature"')) {
      const fullSigUrl = `${backendUrl}${signatureUrl}`;
      const sigImgTag = `<div style="margin: 10px 0;"><img src="${fullSigUrl}" alt="Signature" style="max-height: 70px; max-width: 200px;" /></div>`;
      let inserted = false;
      const patterns = [
        /_{3,}[\s\S]*?\(Signature\)/gi,
        /\[SIGNATURE_PLACEHOLDER\]/gi,
        /\[SIGNATURE\]/gi,
        /<em>\(Signature\)<\/em>/gi,
        /\(Signature\)/gi,
      ];
      for (const pat of patterns) {
        if (pat.test(printContent)) {
          if (!inserted) {
            printContent = printContent.replace(pat, sigImgTag);
            inserted = true;
          }
          printContent = printContent.replace(pat, '');
        }
      }
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Application Letter</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Times New Roman', Times, serif;
            font-size: 14pt;
            line-height: 1.6;
            max-width: 8.5in;
            margin: 0 auto;
            padding: 0.5in 0.75in;
          }
          h1, h2, h3 { font-size: 16pt; margin: 0.5em 0; }
          p { margin: 0 0 0.6em 0; }
          strong { font-weight: bold; }
          u { text-decoration: underline; }
          img { max-width: 200px; max-height: 80px; }
          @media print {
            body { margin: 0; padding: 0.4in 0.6in; }
            @page { margin: 0.4in; size: A4; }
          }
        </style>
      </head>
      <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Load teachers' work output submissions
  const loadTeachersWorkOutput = async () => {
    setLoadingWorkOutput(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/teachers-work-output`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeachersWorkOutput(data.teachers_work_output || []);
        
        // Initialize verification state from existing data
        const verifications = {};
        (data.teachers_work_output || []).forEach(teacher => {
          verifications[teacher.teacher_id] = teacher.verification || {};
        });
        setWorkOutputVerifications(verifications);
      }
    } catch (error) {
      console.error('Error loading teachers work output:', error);
    } finally {
      setLoadingWorkOutput(false);
    }
  };

  // Update verification status locally
  const updateVerification = (teacherId, subject, week, status, comment = '') => {
    setWorkOutputVerifications(prev => {
      const updated = { ...prev };
      if (!updated[teacherId]) updated[teacherId] = {};
      if (!updated[teacherId][subject]) updated[teacherId][subject] = {};
      updated[teacherId][subject][week] = { status, comment };
      return updated;
    });
  };

  // Get verification status for display
  const getVerification = (teacherId, subject, week) => {
    return workOutputVerifications[teacherId]?.[subject]?.[week] || null;
  };

  // Save verification to backend
  const saveVerification = async (teacherId, subject, week, status, comment = '') => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/headteacher/verify-work-output`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ teacher_id: teacherId, subject, week, status, comment })
      });
      
      if (response.ok) {
        toast.success(`Marked as ${status === 'compliant' ? 'Compliant' : 'Non-Compliant'}`);
        updateVerification(teacherId, subject, week, status, comment);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving verification:', error);
      toast.error('Failed to save verification');
    }
  };

  // Calculate compliant total for a teacher's subject
  const calculateCompliantTotal = (teacherId, workOutput, subject) => {
    if (!workOutput[subject]) return 0;
    return workOutputWeeks.reduce((sum, week) => {
      const value = parseInt(workOutput[subject][week]) || 0;
      const verification = getVerification(teacherId, subject, week);
      if (value > 0 && verification?.status === 'compliant') {
        return sum + value;
      }
      return sum;
    }, 0);
  };

  useEffect(() => {
    loadDashboard();
    loadWeeklyStats();
    checkSISOConnection();
    loadTeacherApplications();
    loadMyApplications();
    loadUserProfile(); // Load profile on initial load for signature/stamp in letter preview
    loadNotifications(); // Load lesson notes notifications
    loadUserNotifications(); // Load user notifications (payment links, etc.)
    
    // Refresh notifications every 60 seconds
    const notificationInterval = setInterval(loadNotifications, 60000);
    const userNotificationInterval = setInterval(loadUserNotifications, 60000);
    
    // Handle payment success from escalation
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const paymentRef = urlParams.get('ref');
    if (paymentStatus === 'success' && paymentRef) {
      toast.success('🎉 Payment Successful! Your application has been submitted to the District Office.');
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      loadUserNotifications(); // Refresh notifications
    }
    
    return () => {
      clearInterval(notificationInterval);
      clearInterval(userNotificationInterval);
    };
  }, []);

  // Calculate new notes count whenever weeklyStats changes
  useEffect(() => {
    if (weeklyStats?.submissions && weeklyStats.submissions.length > 0) {
      if (!lastViewedTimestamp) {
        // First time viewing - all submissions are "new"
        setNewNotesCount(weeklyStats.submissions.length);
      } else {
        // Count submissions newer than last viewed timestamp
        const newCount = weeklyStats.submissions.filter(sub => {
          const subDate = new Date(sub.submitted_at || sub.created_at);
          return subDate > lastViewedTimestamp;
        }).length;
        setNewNotesCount(newCount);
      }
    } else {
      setNewNotesCount(0);
    }
  }, [weeklyStats, lastViewedTimestamp]);

  // Handle clicking on "Lesson Notes Received" tab
  const handleNotesTabClick = () => {
    const now = new Date();
    setLastViewedTimestamp(now);
    localStorage.setItem('headteacher_notes_last_viewed', now.toISOString());
    setNewNotesCount(0);
  };

  // Load user profile
  const loadUserProfile = async () => {
    setProfileLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/user/profile`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.profile);
        setProfileFormData({
          town: data.profile.town || '',
          phone: data.profile.phone || '',
          file_number: data.profile.file_number || '',
          mobile_money_number: data.profile.mobile_money_number || '',
          mobile_money_network: data.profile.mobile_money_network || '',
          school_name: data.profile.school_name || '',
          district_name: data.profile.district_name || '',
          region_name: data.profile.region_name || '',
          regional_file_number: data.profile.regional_file_number || '',
          staff_id: data.profile.staff_id || ''
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  // Upload profile picture
  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingProfilePic(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const formData = new FormData();
      formData.append('file', file);

      const response = await authenticatedFetch(`${backendUrl}/api/user/upload-profile-picture`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Profile picture uploaded successfully!');
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error(error.message || 'Failed to upload profile picture');
    } finally {
      setUploadingProfilePic(false);
    }
  };

  // Upload digital signature
  const handleSignatureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingSignature(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const formData = new FormData();
      formData.append('file', file);

      const response = await authenticatedFetch(`${backendUrl}/api/user/upload-signature`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Digital signature uploaded successfully!');
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload');
      }
    } catch (error) {
      console.error('Error uploading signature:', error);
      toast.error(error.message || 'Failed to upload signature');
    } finally {
      setUploadingSignature(false);
    }
  };

  // Upload digital stamp (Headteacher and SISO only)
  const handleStampUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingStamp(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const formData = new FormData();
      formData.append('file', file);

      const response = await authenticatedFetch(`${backendUrl}/api/user/upload-stamp`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Digital stamp uploaded successfully!');
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload');
      }
    } catch (error) {
      console.error('Error uploading stamp:', error);
      toast.error(error.message || 'Failed to upload stamp');
    } finally {
      setUploadingStamp(false);
    }
  };

  // Save profile details
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/user/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileFormData),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Profile updated successfully!');
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.message || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // Delete upload
  const handleDeleteUpload = async (uploadType) => {
    if (!window.confirm(`Are you sure you want to delete your ${uploadType.replace('_', ' ')}?`)) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/user/delete-upload/${uploadType}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`${uploadType.replace('_', ' ')} deleted successfully!`);
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting upload:', error);
      toast.error(error.message || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-600 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700">Loading Dashboard...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        
        {/* Header - Mobile Responsive */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="w-full sm:w-auto">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Welcome, {dashboardData?.user?.name}
            </h1>
            <div className="flex items-center gap-2">
              <p className="text-sm sm:text-base text-gray-600">
                {dashboardData?.user?.school ? `${dashboardData?.user?.school} - ` : ''}Headteacher Dashboard
              </p>
              {dataFromCache && (
                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                  <Clock className="h-3 w-3 mr-1" />
                  Cached
                </Badge>
              )}
              {isOffline && (
                <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-300">
                  Offline
                </Badge>
              )}
            </div>
          </div>
          
          {/* Buttons Container - Two rows on mobile */}
          <div className="w-full sm:w-auto space-y-2 sm:space-y-0">
            {/* Row 1: Create & Connect SISO */}
            <div className="flex gap-2 sm:gap-3 justify-end">
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm px-3 sm:px-4">
                  <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                  <span>Create</span>
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-[95vw] sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-lg sm:text-xl">Create New Teacher Folder</DialogTitle>
                <DialogDescription className="text-sm">
                  Create a folder for lesson notes management. Enter the teacher's name and they can connect using the provided token.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit(onCreateFolder)}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Teacher Name</label>
                    <Input
                      {...register('teacher_name')}
                      placeholder="Enter teacher's full name"
                      className="w-full"
                    />
                    {errors.teacher_name && (
                      <p className="text-sm text-red-500 mt-1">{errors.teacher_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Class/Subject Name
                    </label>
                    <Input
                      {...register('class_or_subject')}
                      placeholder="e.g., KG 1, Class 3, JHS 2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      For KG/Primary: Use class name. For JHS: Use teacher name (not subject). The teacher will receive a connect token to upload lesson notes.
                    </p>
                    {errors.class_or_subject && (
                      <p className="text-sm text-red-500 mt-1">{errors.class_or_subject.message}</p>
                    )}
                  </div>
                </div>
                
                <DialogFooter className="mt-6 flex-col sm:flex-row gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)} className="w-full sm:w-auto">
                    Cancel
                  </Button>
                  <Button type="submit" className="w-full sm:w-auto">Create Teacher Folder</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

            {/* Connect To SISO Button */}
            <Dialog open={isConnectSISODialogOpen} onOpenChange={setIsConnectSISODialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant={sisoConnection?.connected ? "outline" : "default"}
                  className={sisoConnection?.connected 
                    ? "border-green-600 text-green-600 hover:bg-green-50 text-xs sm:text-sm px-3 sm:px-4" 
                    : "bg-green-600 hover:bg-green-700 text-xs sm:text-sm px-3 sm:px-4"
                  }
                >
                  <Link2 className="h-4 w-4 mr-1 sm:mr-2" />
                  <span>{sisoConnection?.connected ? 'SISO' : 'SISO'}</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-green-600" />
                    {sisoConnection?.connected ? 'SISO Connection' : 'Connect To SISO'}
                  </DialogTitle>
                  <DialogDescription>
                    {sisoConnection?.connected 
                      ? 'Your school is connected to a SISO (School Improvement Support Officer).'
                      : 'Enter the connect token provided by your SISO to link your school.'
                    }
                  </DialogDescription>
                </DialogHeader>
                
                {sisoConnection?.connected ? (
                  <div className="space-y-4 py-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="font-semibold text-green-800">Connected</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-gray-600">SISO:</span> <span className="font-medium">{sisoConnection.siso_name}</span></p>
                        <p><span className="text-gray-600">Circuit:</span> <span className="font-medium">{sisoConnection.circuit_name}</span></p>
                        <p><span className="text-gray-600">District:</span> <span className="font-medium">{sisoConnection.district_name}</span></p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Your term reports are now accessible to your SISO for circuit-level reporting.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4 py-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Connect Token</label>
                      <Input
                        value={sisoConnectToken}
                        onChange={(e) => setSisoConnectToken(e.target.value)}
                        placeholder="Paste the token from your SISO"
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Get this token from your School Improvement Support Officer (SISO)
                      </p>
                    </div>
                  </div>
                )}
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsConnectSISODialogOpen(false)}>
                    {sisoConnection?.connected ? 'Close' : 'Cancel'}
                  </Button>
                  {!sisoConnection?.connected && (
                    <Button 
                      onClick={connectToSISO} 
                      disabled={connectingSISO || !sisoConnectToken.trim()}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {connectingSISO ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        'Connect'
                      )}
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>

            {/* Row 2: Alerts & Logout */}
            <div className="flex gap-2 sm:gap-3 justify-end">
            {/* Notification Bell */}
            <div className="relative">
              <Button 
                onClick={() => {
                  const willOpen = !showNotificationPanel;
                  setShowNotificationPanel(willOpen);
                  if (willOpen && unreadNotificationCount > 0) markAllNotificationsRead();
                }}
                variant="outline" 
                className="relative text-xs sm:text-sm px-3 sm:px-4"
                data-testid="notification-bell-btn"
              >
                <Bell className="h-4 w-4 mr-1 sm:mr-2" />
                <span>Alerts</span>
                {unreadNotificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                  </span>
                )}
              </Button>
              
              {/* Notification Dropdown Panel */}
              {showNotificationPanel && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[400px] overflow-hidden" data-testid="notification-panel">
                  <div className="p-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-lg flex justify-between items-center">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Bell className="h-4 w-4" />
                      Notifications
                      {unreadNotificationCount > 0 && (
                        <Badge className="bg-white/20 text-white">{unreadNotificationCount} new</Badge>
                      )}
                    </h3>
                    {unreadNotificationCount > 0 && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-white hover:bg-white/20 text-xs"
                        onClick={markAllNotificationsRead}
                      >
                        Mark all read
                      </Button>
                    )}
                  </div>
                  
                  <div className="overflow-y-auto max-h-[320px]">
                    {loadingNotifications ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="py-8 text-center text-gray-500">
                        <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                        <p>No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div 
                          key={notification.id}
                          className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${!notification.is_read ? 'bg-purple-50' : ''}`}
                          onClick={() => !notification.is_read && markNotificationRead(notification.id)}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                              notification.notification_type === 'delivery_status' 
                                ? notification.related_data?.delivery_status === 'delivered' 
                                  ? 'bg-green-100' 
                                  : 'bg-amber-100'
                                : 'bg-blue-100'
                            }`}>
                              {notification.notification_type === 'delivery_status' ? (
                                notification.related_data?.delivery_status === 'delivered' 
                                  ? <CheckCircle className="h-4 w-4 text-green-600" />
                                  : <AlertCircle className="h-4 w-4 text-amber-600" />
                              ) : (
                                <MessageSquare className="h-4 w-4 text-blue-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-gray-500 mt-1 line-clamp-2">{notification.message}</p>
                              <p className="text-[10px] text-gray-400 mt-1">
                                {notification.teacher_name} • {new Date(notification.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            {!notification.is_read && (
                              <div className="w-2 h-2 bg-purple-600 rounded-full shrink-0 mt-1"></div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Notification Bell (for Payment Links, SISO Escalations) */}
            <div className="relative">
              <Button 
                onClick={() => setShowUserNotificationPanel(!showUserNotificationPanel)}
                variant="outline" 
                className="relative flex-1 sm:flex-initial text-sm sm:text-base"
                data-testid="user-notification-bell-btn"
              >
                <CreditCard className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Payments</span>
                {unreadUserNotificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadUserNotificationCount > 9 ? '9+' : unreadUserNotificationCount}
                  </span>
                )}
              </Button>
              
              {/* User Notification Dropdown Panel */}
              {showUserNotificationPanel && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[400px] overflow-hidden" data-testid="user-notification-panel">
                  <div className="p-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-t-lg flex justify-between items-center">
                    <h3 className="font-semibold flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Payments
                      {unreadUserNotificationCount > 0 && (
                        <Badge className="bg-white/20 text-white">{unreadUserNotificationCount} new</Badge>
                      )}
                    </h3>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-white hover:bg-white/20 text-xs h-6 w-6 p-0"
                      onClick={() => setShowUserNotificationPanel(false)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="overflow-y-auto max-h-[320px]">
                    {loadingUserNotifications ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                      </div>
                    ) : userNotifications.length === 0 ? (
                      <div className="py-8 text-center text-gray-500">
                        <CreditCard className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                        <p>No payment notifications</p>
                      </div>
                    ) : (
                      userNotifications.map((notification) => (
                        <div 
                          key={notification.id}
                          className={`p-3 border-b border-gray-100 ${!notification.read ? 'bg-amber-50' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                              notification.notification_type === 'payment_request' 
                                ? 'bg-amber-100' 
                                : 'bg-blue-100'
                            }`}>
                              {notification.notification_type === 'payment_request' ? (
                                <CreditCard className="h-4 w-4 text-amber-600" />
                              ) : (
                                <Bell className="h-4 w-4 text-blue-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-600'}`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-gray-500 mt-1 line-clamp-3">{notification.message}</p>
                              <p className="text-[10px] text-gray-400 mt-1">
                                {new Date(notification.created_at).toLocaleDateString()} {new Date(notification.created_at).toLocaleTimeString()}
                              </p>
                              
                              {/* Payment Action Button */}
                              {notification.notification_type === 'payment_request' && notification.action_url && (
                                <Button 
                                  size="sm" 
                                  className="mt-2 bg-amber-500 hover:bg-amber-600 text-white"
                                  onClick={() => handlePaymentClick(notification)}
                                  data-testid={`pay-btn-${notification.id}`}
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  Pay GHS {notification.related_data?.amount?.toFixed(2) || '25.00'}
                                </Button>
                              )}
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-amber-600 rounded-full shrink-0 mt-1"></div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <Button onClick={handleLogout} variant="outline" className="text-red-600 border-red-600 hover:bg-red-50 text-xs sm:text-sm px-3 sm:px-4">
              <LogOut className="h-4 w-4 mr-1 sm:mr-2" />
              <span>Logout</span>
            </Button>
            </div>
          </div>
        </div>

        {/* PWA Install Prompt */}
        <InstallPWA />

        {/* User Tutorial Guide - Collapsible */}
        <Dialog>
          <DialogTrigger asChild>
            <Button className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold py-6 text-base sm:text-lg shadow-lg">
              <BookOpen className="h-5 w-5 mr-2" />
              Learn How To Use The App
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-900 text-xl sm:text-2xl">
                <BookOpen className="h-6 w-6" />
                How to Use Headteacher Dashboard
              </DialogTitle>
              <DialogDescription className="text-blue-700">
                Follow these simple steps to create folders, connect with teachers, and manage lesson notes
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Step 1 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    1
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Click on Create Teacher Folder</h4>
                  <p className="text-sm text-gray-700">
                    Start by clicking the <span className="font-semibold text-blue-600">"Create Teacher Folder"</span> button at the top of your dashboard. This opens the folder creation form.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    2
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Enter Teacher Information</h4>
                  <p className="text-sm text-gray-700 mb-2">
                    Fill in the required information:
                  </p>
                  <div className="ml-4 space-y-2">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-orange-600">• For KG/Primary Teachers:</span> Enter teacher's name and class (e.g., <span className="font-mono bg-gray-100 px-1">KG 1</span>, <span className="font-mono bg-gray-100 px-1">Class 6</span>)
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-indigo-600">• For JHS Teachers:</span> Enter teacher's name and just the JHS level (e.g., <span className="font-mono bg-gray-100 px-1">JHS 1</span>, <span className="font-mono bg-gray-100 px-1">JHS 2</span>, <span className="font-mono bg-gray-100 px-1">JHS 3</span>)
                    </p>
                  </div>
                  <p className="text-sm text-gray-700 mt-2">
                    Then click <span className="font-semibold text-blue-600">"Create Teacher Folder"</span> to complete the process.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    3
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Copy and Share Connect Token</h4>
                  <p className="text-sm text-gray-700">
                    Once the folder is created, a <span className="font-semibold text-purple-600">connect token code</span> is generated. Click the <span className="font-semibold">Copy</span> icon to copy the token, then share this code with the teacher. They will need this token to connect their dashboard with your folder.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    4
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Manage Received Lesson Notes</h4>
                  <p className="text-sm text-gray-700">
                    Click on the <span className="font-semibold text-green-600">"Lesson Notes Submitted"</span> tab to view and manage all lesson notes that teachers have submitted to you. You can:
                  </p>
                  <div className="ml-4 mt-2 space-y-1">
                    <p className="text-sm text-gray-700">• View weekly submission statistics</p>
                    <p className="text-sm text-gray-700">• See which teachers have submitted</p>
                    <p className="text-sm text-gray-700">• Download submitted lesson notes</p>
                    <p className="text-sm text-gray-700">• Track submission dates and times</p>
                  </div>
                  <p className="text-sm text-gray-700 mt-2">
                    <span className="font-semibold text-green-600">✅ That's it!</span> You can now effectively manage all your teachers' lesson notes.
                  </p>
                </div>
              </div>

              {/* Step 5 - Add to Home Screen */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    5
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">Add App to Home Screen (Optional)</h4>
                  <p className="text-sm text-gray-700 mb-2">
                    For quick access without typing the domain name every time, install the app on your device:
                  </p>
                  <div className="ml-4 space-y-2">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-blue-600">• On Android/Desktop:</span> Click the <span className="font-semibold">"Add to Home Screen"</span> button that appears on your dashboard, or click the install icon in your browser's address bar.
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-blue-600">• On iPhone/iPad:</span>
                      <br />
                      1. Tap the Share button 📤 at the bottom
                      <br />
                      2. Scroll down and tap "Add to Home Screen"
                      <br />
                      3. Tap "Add" to confirm
                    </p>
                  </div>
                  <p className="text-sm text-gray-700 mt-2">
                    <span className="font-semibold text-purple-600">🎉 Enjoy!</span> Access the app like a native app from your home screen.
                  </p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Main Tabs - Mobile Responsive */}
        <Tabs defaultValue="folders" className="w-full">
          <TabsList className="grid w-full grid-cols-3 sm:grid-cols-5 h-auto gap-1">
            <TabsTrigger value="folders" className="text-xs sm:text-sm py-2 sm:py-3">
              Folders
            </TabsTrigger>
            <TabsTrigger 
              value="stats" 
              className="text-xs sm:text-sm py-2 sm:py-3 relative"
              onClick={handleNotesTabClick}
              data-testid="lesson-notes-received-tab"
            >
              Notes Received
              {/* Notification badge for new submissions */}
              {newNotesCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold animate-pulse">
                  {newNotesCount > 99 ? '99+' : newNotesCount}
                </span>
              )}
            </TabsTrigger>
            {authUser?.sector !== 'private' && (
            <TabsTrigger 
              value="applications" 
              className="text-xs sm:text-sm py-2 sm:py-3 relative"
              onClick={loadTeacherApplications}
              data-testid="applications-tab"
            >
              Applications
              {newTeacherAppsCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 bg-amber-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold animate-pulse">
                  {newTeacherAppsCount > 99 ? '99+' : newTeacherAppsCount}
                </span>
              )}
            </TabsTrigger>
            )}
            <TabsTrigger 
              value="report" 
              className="text-xs sm:text-sm py-2 sm:py-3"
              onClick={() => {
                if (teachersForReport.length === 0) {
                  loadTeachersForReport();
                }
              }}
            >
              Create Report
            </TabsTrigger>
            <TabsTrigger 
              value="profile" 
              className="text-xs sm:text-sm py-2 sm:py-3"
              onClick={loadUserProfile}
              data-testid="profile-tab"
            >
              Profile
            </TabsTrigger>
            {authUser?.sector !== 'private' && (
            <TabsTrigger value="promotion_exams" className="text-xs sm:text-sm py-2 sm:py-3">
              <GraduationCap className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Promotion Exams
            </TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="folders" className="space-y-6">
            
            {/* Folders grouped by education level */}
            {['kg', 'primary', 'jhs'].map((level) => (
              <Card key={level}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <School className="h-5 w-5" />
                    {getEducationLevelName(level)}
                  </CardTitle>
                  <CardDescription>
                    Folders for {getEducationLevelName(level).toLowerCase()} teachers
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  
                  {dashboardData?.folders?.[level]?.length > 0 ? (
                    <div className="space-y-4">
                      {dashboardData.folders[level].map((folder) => (
                        <Card key={folder.id} className="border-l-4 border-l-blue-500">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleFolderExpansion(folder.id)}
                                  >
                                    {expandedFolders[folder.id] ? 
                                      <FolderOpen className="h-4 w-4" /> : 
                                      <Folder className="h-4 w-4" />
                                    }
                                  </Button>
                                  <h3 className="font-semibold">{folder.class_or_subject}</h3>
                                  <Badge variant={folder.is_active ? "default" : "secondary"}>
                                    {folder.is_active ? "Active" : "Revoked"}
                                  </Badge>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-gray-600">
                                      <strong>Teacher:</strong> {folder.teacher_name}
                                      {folder.teacher_id ? (
                                        <Badge className="ml-2 bg-green-100 text-green-800">Connected</Badge>
                                      ) : (
                                        <Badge variant="outline" className="ml-2 text-amber-600 border-amber-600">Not Connected</Badge>
                                      )}
                                    </p>
                                    <p className="text-gray-600">
                                      <strong>Created:</strong> {new Date(folder.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-gray-600 mb-2">
                                      <strong>Connect Token:</strong>
                                    </p>
                                    <div className="flex gap-2">
                                      <Input
                                        value={folder.connect_token}
                                        readOnly
                                        className="text-xs font-mono"
                                      />
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => copyToken(folder.connect_token)}
                                        title="Copy token"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => toggleFolderToken(folder.id)}
                                        title={folder.is_active ? "Revoke token" : "Activate token"}
                                      >
                                        {folder.is_active ? 
                                          <EyeOff className="h-3 w-3" /> : 
                                          <Eye className="h-3 w-3" />
                                        }
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => deleteFolder(folder.id, folder.teacher_name)}
                                        title="Delete folder"
                                        className="border-red-600 text-red-600 hover:bg-red-50"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>

                                {/* Folder Stats */}
                                {/* Expanded folder contents */}
                                {expandedFolders[folder.id] && folderContents?.folder?.id === folder.id && (
                                  <div className="mt-4 border-t pt-4">
                                    <h4 className="font-medium mb-3 flex items-center gap-2">
                                      <FileText className="h-4 w-4" />
                                      Lesson Notes ({folderContents.lesson_notes?.length || 0})
                                    </h4>
                                    
                                    {folderContents.lesson_notes?.length > 0 ? (
                                      <div className="space-y-2">
                                        {folderContents.lesson_notes.map((note) => (
                                          <div key={note.id} className="p-3 bg-white border rounded-md">
                                            <div className="flex justify-between items-start">
                                              <div>
                                                <p className="font-medium">{note.subject}</p>
                                                <p className="text-sm text-gray-600">
                                                  Class: {note.class_level} • Week: {note.week}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                  By: {note.teacher_name} • {new Date(note.created_at).toLocaleDateString()}
                                                </p>
                                              </div>
                                              <Button 
                                                size="sm" 
                                                variant="outline"
                                                onClick={() => window.open(note.file_url, '_blank')}
                                              >
                                                <Eye className="h-3 w-3 mr-1" />
                                                Preview
                                              </Button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-gray-500 text-sm">No lesson notes uploaded yet</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Folder className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No folders created for {getEducationLevelName(level).toLowerCase()} yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  {weeklyStats?.active_term_name || 'Current Term'} Lesson Notes Submitted
                </CardTitle>
                <CardDescription>
                  Track lesson notes submissions per teacher every week
                </CardDescription>
              </CardHeader>
              <CardContent>
                {weeklyStats?.weekly_stats ? (
                  <div className="space-y-4">
                    {/* Mobile: Horizontal scroll for table */}
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                      <div className="inline-block min-w-full align-middle">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs sm:text-sm">Teacher</TableHead>
                              {weeklyStats.weeks_displayed?.map((weekLabel) => (
                                <TableHead key={weekLabel} className="text-xs sm:text-sm">{weekLabel}</TableHead>
                              ))}
                              <TableHead className="text-xs sm:text-sm">Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {Object.entries(weeklyStats.weekly_stats).map(([teacherName, weekData]) => {
                              const total = Object.values(weekData).reduce((sum, count) => sum + count, 0);
                              return (
                                <TableRow key={teacherName}>
                                  <TableCell className="font-medium text-xs sm:text-sm">{teacherName}</TableCell>
                                  {weeklyStats.weeks_displayed?.map((weekLabel) => (
                                    <TableCell key={weekLabel}>
                                      <Badge variant={weekData[weekLabel] > 0 ? "default" : "outline"} className="text-xs">
                                        {weekData[weekLabel] || 0}
                                      </Badge>
                                    </TableCell>
                                  ))}
                                  <TableCell>
                                    <Badge variant="secondary" className="text-xs">{total}</Badge>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <TrendingUp className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">Loading statistics...</p>
                  </div>
                )}
                
                {/* Display Submitted Lesson Notes Grouped by Teacher and Week */}
                {weeklyStats?.submissions && weeklyStats.submissions.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-4">Received Lesson Notes ({weeklyStats.submissions.length})</h3>
                    <div className="space-y-6">
                      {(() => {
                        // Group submissions by teacher
                        const byTeacher = weeklyStats.submissions.reduce((acc, sub) => {
                          const teacher = sub.teacher_name || 'Unknown Teacher';
                          if (!acc[teacher]) acc[teacher] = [];
                          acc[teacher].push(sub);
                          return acc;
                        }, {});
                        
                        return Object.entries(byTeacher).map(([teacherName, teacherSubmissions]) => {
                          // Group by week for this teacher
                          const byWeek = teacherSubmissions.reduce((acc, sub) => {
                            const week = sub.week || 0;
                            if (!acc[week]) acc[week] = [];
                            acc[week].push(sub);
                            return acc;
                          }, {});
                          
                          return (
                            <Card key={teacherName} className="border-2">
                              <CardHeader className="bg-blue-50">
                                <CardTitle className="flex items-center gap-2">
                                  <Users className="h-5 w-5 text-blue-600" />
                                  {teacherName}
                                  <Badge variant="secondary" className="ml-auto">
                                    {teacherSubmissions.length} submission{teacherSubmissions.length !== 1 ? 's' : ''}
                                  </Badge>
                                </CardTitle>
                                <CardDescription className="text-sm">
                                  {teacherSubmissions[0]?.teacher_email}
                                </CardDescription>
                              </CardHeader>
                              <CardContent className="p-4">
                                <div className="space-y-3">
                                  {Object.entries(byWeek)
                                    .sort(([a], [b]) => Number(b) - Number(a)) // Sort weeks descending (newest first)
                                    .map(([week, weekSubmissions]) => {
                                      const isExpanded = isWeekExpanded(teacherName, week);
                                      const notesCount = weekSubmissions.length;
                                      
                                      return (
                                        <div key={week} className="ml-2">
                                          {/* Clickable Week Folder Header */}
                                          <button
                                            onClick={() => toggleWeekFolder(teacherName, week)}
                                            className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 rounded-lg border border-green-200 transition-all duration-200 group"
                                          >
                                            <div className="flex items-center gap-2 sm:gap-3">
                                              {isExpanded ? (
                                                <FolderOpen className="h-5 w-5 text-green-600" />
                                              ) : (
                                                <Folder className="h-5 w-5 text-green-600" />
                                              )}
                                              <span className="font-medium text-sm sm:text-base text-gray-800">
                                                Week {week} - Term {weekSubmissions[0]?.term}
                                              </span>
                                              <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                                                {notesCount} {notesCount === 1 ? 'note' : 'notes'}
                                              </Badge>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs text-gray-500 hidden sm:inline">
                                                {isExpanded ? 'Click to collapse' : 'Click to expand'}
                                              </span>
                                              {isExpanded ? (
                                                <ChevronDown className="h-5 w-5 text-gray-500 group-hover:text-green-600 transition-colors" />
                                              ) : (
                                                <ChevronRight className="h-5 w-5 text-gray-500 group-hover:text-green-600 transition-colors" />
                                              )}
                                            </div>
                                          </button>
                                          
                                          {/* Expandable Notes Container */}
                                          {isExpanded && (
                                            <div className="mt-2 space-y-2 ml-2 sm:ml-4 animate-in slide-in-from-top-2 duration-200">
                                              {weekSubmissions.map((submission) => (
                                                <div 
                                                  key={submission.id}
                                                  className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 bg-gray-50 p-3 rounded-lg border-l-4 border-l-green-500"
                                                >
                                                  <div className="flex-1">
                                                    <p className="font-medium text-sm">{submission.file_name}</p>
                                                    <div className="text-xs text-gray-600 mt-1">
                                                      <span><strong>Subject:</strong> {submission.subject}</span>
                                                      <span className="mx-2">|</span>
                                                      <span><strong>Class:</strong> {submission.class_name}</span>
                                                      <span className="mx-2">|</span>
                                                      <span className="text-gray-500">
                                                        {new Date(submission.submitted_at).toLocaleDateString()} at {new Date(submission.submitted_at).toLocaleTimeString()}
                                                      </span>
                                                    </div>
                                                  </div>
                                                  <div className="flex gap-2 items-center flex-wrap">
                                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">
                                                      {submission.education_level?.toUpperCase()}
                                                    </Badge>
                                                    
                                                    <Button
                                                      size="sm"
                                                      variant="outline"
                                                      onClick={() => readSubmission(submission.id, submission.file_name)}
                                                      className="border-blue-600 text-blue-600 hover:bg-blue-50"
                                                    >
                                                      <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                      <span className="text-xs">Read</span>
                                                    </Button>
                                                    
                                                    <Button
                                                      size="sm"
                                                      variant="outline"
                                                      onClick={() => downloadSubmission(submission.id, submission.file_name)}
                                                      className="border-green-600 text-green-600 hover:bg-green-50"
                                                    >
                                                      <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                      <span className="text-xs">Download</span>
                                                    </Button>
                                                    
                                                    {/* Comment Button */}
                                                    <Button
                                                      variant="outline"
                                                      size="sm"
                                                      onClick={() => openCommentDialog(submission)}
                                                      className={`border-amber-600 text-amber-600 hover:bg-amber-50 ${submission.headteacher_comment ? 'bg-amber-50' : ''}`}
                                                    >
                                                      <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                                      <span className="text-xs">{submission.headteacher_comment ? 'Edit' : 'Comment'}</span>
                                                    </Button>
                                                    
                                                    <div className="flex items-center gap-2">
                                                      <Checkbox
                                                        checked={submission.vetted || false}
                                                        onCheckedChange={() => vetSubmission(submission.id, submission.vetted)}
                                                        id={`vet-${submission.id}`}
                                                      />
                                                      <label 
                                                        htmlFor={`vet-${submission.id}`}
                                                        className="text-xs text-gray-700 cursor-pointer"
                                                      >
                                                        Vet
                                                      </label>
                                                    </div>
                                                  </div>
                                                  
                                                  {/* Show existing comment if any */}
                                                  {submission.headteacher_comment && (
                                                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                                                      <p className="text-xs text-amber-800">
                                                        <strong>Comment:</strong> {submission.headteacher_comment}
                                                      </p>
                                                      {submission.comment_date && (
                                                        <p className="text-[10px] text-amber-600 mt-1">
                                                          {new Date(submission.comment_date).toLocaleDateString()}
                                                        </p>
                                                      )}
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Create Report Tab */}
          <TabsContent value="report" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <ClipboardList className="h-4 w-4 sm:h-5 sm:w-5" />
                  Create Term Report
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Generate Start of Term or End of Term report for SISO
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                {/* Report Type Selection */}
                <div className="mb-4 sm:mb-6">
                  <label className="block text-xs sm:text-sm font-medium mb-2">Report Type</label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={reportType === 'start_of_term' ? 'default' : 'outline'}
                      onClick={() => setReportType('start_of_term')}
                      className={`flex-1 ${reportType === 'start_of_term' ? 'bg-blue-600' : ''}`}
                    >
                      Start of Term
                    </Button>
                    <Button
                      type="button"
                      variant={reportType === 'end_of_term' ? 'default' : 'outline'}
                      onClick={() => setReportType('end_of_term')}
                      className={`flex-1 ${reportType === 'end_of_term' ? 'bg-green-600' : ''}`}
                    >
                      End of Term
                    </Button>
                  </div>
                </div>

                {/* Term and Academic Year Selection */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2">Term</label>
                    <Select value={termName} onValueChange={setTermName}>
                      <SelectTrigger className="h-9 sm:h-10 text-sm">
                        <SelectValue placeholder="Select term" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Term 1">Term 1</SelectItem>
                        <SelectItem value="Term 2">Term 2</SelectItem>
                        <SelectItem value="Term 3">Term 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2">Academic Year</label>
                    <Input 
                      value={academicYear} 
                      onChange={(e) => setAcademicYear(e.target.value)}
                      placeholder="e.g., 2024/2025"
                      className="h-9 sm:h-10 text-sm"
                    />
                  </div>
                </div>

                {/* Include Headteacher Option */}
                <div className="flex items-center gap-2 mb-4 sm:mb-6 p-2 sm:p-3 bg-blue-50 rounded-lg">
                  <Checkbox 
                    id="includeHeadteacher"
                    checked={includeHeadteacher}
                    onCheckedChange={(checked) => {
                      setIncludeHeadteacher(checked);
                      if (checked && !reportData['headteacher']) {
                        setReportData(prev => ({
                          ...prev,
                          'headteacher': {
                            teacher_id: 'headteacher',
                            teacher_name: dashboardData?.user?.name || 'Headteacher',
                            class_or_subject: 'Administration',
                            teacher_rank: '',
                            teacher_portfolio: 'Headteacher',
                            is_present: true,
                            absent_with_permission: '',
                            absent_without_permission: '',
                            total_attendance: '',
                            lesson_notes_received: 'N/A'
                          }
                        }));
                      }
                    }}
                  />
                  <label htmlFor="includeHeadteacher" className="text-xs sm:text-sm font-medium cursor-pointer flex items-center gap-1">
                    <UserPlus className="h-3 w-3 sm:h-4 sm:w-4" />
                    Include myself (Headteacher) in report
                  </label>
                </div>

                {loadingTeachers ? (
                  <div className="flex items-center justify-center py-8 sm:py-12">
                    <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600 text-sm">Loading...</span>
                  </div>
                ) : teachersForReport.length === 0 && !includeHeadteacher ? (
                  <div className="text-center py-8 sm:py-12">
                    <Users className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-gray-400 mb-3 sm:mb-4" />
                    <p className="text-gray-600 text-sm">No teachers found. Create folders first.</p>
                    <Button 
                      className="mt-3 sm:mt-4 text-sm" 
                      size="sm"
                      onClick={loadTeachersForReport}
                    >
                      Refresh
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* START OF TERM REPORT */}
                    {reportType === 'start_of_term' && (
                      <>
                        {/* Teacher Attendance Section */}
                        <div className="space-y-2">
                          <h3 className="font-semibold text-sm border-b pb-2">Teacher Attendance</h3>
                          
                          {/* Headteacher Entry */}
                          {includeHeadteacher && reportData['headteacher'] && (
                            <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <UserPlus className="h-4 w-4 text-blue-600" />
                                <span className="text-sm font-medium">{reportData['headteacher'].teacher_name}</span>
                                <Badge className="bg-blue-100 text-blue-800 text-[10px]">HT</Badge>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                  <Select 
                                    value={reportData['headteacher']?.teacher_rank || ''}
                                    onValueChange={(value) => updateReportField('headteacher', 'teacher_rank', value)}
                                  >
                                    <SelectTrigger className="h-7 w-24 text-xs">
                                      <SelectValue placeholder="Rank" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {teacherRankOptions.map(rank => (
                                        <SelectItem key={rank} value={rank}>{rank}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Select 
                                    value={reportData['headteacher']?.teacher_portfolio || 'Headteacher'}
                                    onValueChange={(value) => updateReportField('headteacher', 'teacher_portfolio', value)}
                                  >
                                    <SelectTrigger className="h-7 w-28 text-xs">
                                      <SelectValue placeholder="Portfolio" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {teacherPortfolioOptions.map(portfolio => (
                                        <SelectItem key={portfolio} value={portfolio}>{portfolio}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="flex items-center gap-3">
                                  <label className="flex items-center gap-1 cursor-pointer">
                                    <Checkbox 
                                      checked={reportData['headteacher']?.is_present === true}
                                      onCheckedChange={() => updateReportField('headteacher', 'is_present', true)}
                                    />
                                    <span className="text-xs text-green-600">Present</span>
                                  </label>
                                  <label className="flex items-center gap-1 cursor-pointer">
                                    <Checkbox 
                                      checked={reportData['headteacher']?.is_present === false}
                                      onCheckedChange={() => updateReportField('headteacher', 'is_present', false)}
                                    />
                                    <span className="text-xs text-red-600">Absent</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Teacher Entries */}
                          {teachersForReport.map((teacher) => (
                            <div key={teacher.teacher_id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg flex-wrap gap-2">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-green-600" />
                                <span className="text-sm font-medium">{teacher.teacher_name}</span>
                                <Badge variant="outline" className="text-[10px]">{teacher.class_or_subject}</Badge>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Select 
                                  value={reportData[teacher.teacher_id]?.teacher_rank || ''}
                                  onValueChange={(value) => updateReportField(teacher.teacher_id, 'teacher_rank', value)}
                                >
                                  <SelectTrigger className="h-7 w-24 text-xs">
                                    <SelectValue placeholder="Rank" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {teacherRankOptions.map(rank => (
                                      <SelectItem key={rank} value={rank}>{rank}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Select 
                                  value={reportData[teacher.teacher_id]?.teacher_portfolio || ''}
                                  onValueChange={(value) => updateReportField(teacher.teacher_id, 'teacher_portfolio', value)}
                                >
                                  <SelectTrigger className="h-7 w-28 text-xs">
                                    <SelectValue placeholder="Portfolio" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {teacherPortfolioOptions.map(portfolio => (
                                      <SelectItem key={portfolio} value={portfolio}>{portfolio}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <div className="flex items-center gap-3">
                                  <label className="flex items-center gap-1 cursor-pointer">
                                    <Checkbox 
                                      checked={reportData[teacher.teacher_id]?.is_present === true}
                                      onCheckedChange={() => updateReportField(teacher.teacher_id, 'is_present', true)}
                                    />
                                    <span className="text-xs text-green-600">Present</span>
                                  </label>
                                  <label className="flex items-center gap-1 cursor-pointer">
                                    <Checkbox 
                                      checked={reportData[teacher.teacher_id]?.is_present === false}
                                      onCheckedChange={() => updateReportField(teacher.teacher_id, 'is_present', false)}
                                    />
                                    <span className="text-xs text-red-600">Absent</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Class Attendance Section */}
                        <div className="space-y-2">
                          <h3 className="font-semibold text-sm border-b pb-2">Class Attendance (Number of Pupils Present)</h3>
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                            {classOptions.map((cls) => (
                              <div key={cls} className="flex flex-col">
                                <label className="text-[10px] text-gray-500 mb-1">{cls}</label>
                                <Input 
                                  type="number"
                                  min="0"
                                  value={classAttendance[cls] || ''}
                                  onChange={(e) => updateClassAttendance(cls, e.target.value)}
                                  placeholder="0"
                                  className="h-8 text-center text-sm"
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Remarks Section */}
                        <div className="space-y-2">
                          <h3 className="font-semibold text-sm border-b pb-2">Headteacher's Remarks</h3>
                          <textarea
                            value={headteacherRemarks}
                            onChange={(e) => setHeadteacherRemarks(e.target.value)}
                            placeholder="Enter challenges, concerns, achievements, or any matters relevant to the school..."
                            className="w-full min-h-[100px] p-3 border rounded-lg text-sm resize-y"
                          />
                        </div>
                      </>
                    )}

                    {/* END OF TERM REPORT */}
                    {reportType === 'end_of_term' && (
                      <div className="space-y-3 sm:space-y-4">
                        {/* Headteacher Entry (if included) */}
                        {includeHeadteacher && reportData['headteacher'] && (
                          <Card className="border-l-4 border-l-blue-500 bg-blue-50/50">
                            <CardContent className="p-3 sm:p-4">
                              <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                                <UserPlus className="h-4 w-4 text-blue-600" />
                                <span className="font-semibold text-sm">{reportData['headteacher'].teacher_name}</span>
                                <Badge className="bg-blue-100 text-blue-800 text-[10px]">Headteacher</Badge>
                              </div>
                              
                              {/* Single Row Layout */}
                              <div className="flex flex-wrap items-center gap-2">
                                <Select 
                                  value={reportData['headteacher']?.teacher_rank || ''}
                                  onValueChange={(value) => updateReportField('headteacher', 'teacher_rank', value)}
                                >
                                  <SelectTrigger className="h-7 w-24 text-xs">
                                    <SelectValue placeholder="Rank" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {teacherRankOptions.map(rank => (
                                      <SelectItem key={rank} value={rank}>{rank}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Select 
                                  value={reportData['headteacher']?.teacher_portfolio || 'Headteacher'}
                                  onValueChange={(value) => updateReportField('headteacher', 'teacher_portfolio', value)}
                                >
                                  <SelectTrigger className="h-7 w-28 text-xs">
                                    <SelectValue placeholder="Portfolio" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {teacherPortfolioOptions.map(portfolio => (
                                      <SelectItem key={portfolio} value={portfolio}>{portfolio}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-gray-500">Abs(P):</span>
                                  <Input 
                                    type="number" 
                                    min="0"
                                    value={reportData['headteacher']?.absent_with_permission ?? ''}
                                    onChange={(e) => updateReportField('headteacher', 'absent_with_permission', e.target.value)}
                                    className="h-7 w-12 text-center text-xs"
                                    placeholder="0"
                                  />
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-gray-500">Abs(NP):</span>
                                  <Input 
                                    type="number" 
                                    min="0"
                                    value={reportData['headteacher']?.absent_without_permission ?? ''}
                                    onChange={(e) => updateReportField('headteacher', 'absent_without_permission', e.target.value)}
                                    className="h-7 w-12 text-center text-xs"
                                    placeholder="0"
                                  />
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-gray-500">Total:</span>
                                  <Input 
                                    type="number" 
                                    min="0"
                                    value={reportData['headteacher']?.total_attendance ?? ''}
                                    onChange={(e) => updateReportField('headteacher', 'total_attendance', e.target.value)}
                                    className="h-7 w-12 text-center text-xs"
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Teacher Entries */}
                        {teachersForReport.map((teacher) => (
                          <Card key={teacher.teacher_id} className="border-l-4 border-l-green-500">
                            <CardContent className="p-3 sm:p-4">
                              <div className="flex items-center gap-2 mb-2 pb-2 border-b flex-wrap">
                                <Users className="h-4 w-4 text-green-600 shrink-0" />
                                <span className="font-semibold text-sm">{teacher.teacher_name}</span>
                                <Badge variant="outline" className="text-[10px]">{teacher.class_or_subject}</Badge>
                              </div>
                              
                              {/* Single Row: Rank, Portfolio, Attendance */}
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <Select 
                                  value={reportData[teacher.teacher_id]?.teacher_rank || ''}
                                  onValueChange={(value) => updateReportField(teacher.teacher_id, 'teacher_rank', value)}
                                >
                                  <SelectTrigger className="h-7 w-24 text-xs">
                                    <SelectValue placeholder="Rank" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {teacherRankOptions.map(rank => (
                                      <SelectItem key={rank} value={rank}>{rank}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Select 
                                  value={reportData[teacher.teacher_id]?.teacher_portfolio || ''}
                                  onValueChange={(value) => updateReportField(teacher.teacher_id, 'teacher_portfolio', value)}
                                >
                                  <SelectTrigger className="h-7 w-28 text-xs">
                                    <SelectValue placeholder="Portfolio" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {teacherPortfolioOptions.map(portfolio => (
                                      <SelectItem key={portfolio} value={portfolio}>{portfolio}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-gray-500">Abs(P):</span>
                                  <Input 
                                    type="number" 
                                    min="0"
                                    value={reportData[teacher.teacher_id]?.absent_with_permission ?? ''}
                                    onChange={(e) => updateReportField(teacher.teacher_id, 'absent_with_permission', e.target.value)}
                                    className="h-7 w-12 text-center text-xs"
                                    placeholder="0"
                                  />
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-gray-500">Abs(NP):</span>
                                  <Input 
                                    type="number" 
                                    min="0"
                                    value={reportData[teacher.teacher_id]?.absent_without_permission ?? ''}
                                    onChange={(e) => updateReportField(teacher.teacher_id, 'absent_without_permission', e.target.value)}
                                    className="h-7 w-12 text-center text-xs"
                                    placeholder="0"
                                  />
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-gray-500">Total:</span>
                                  <Input 
                                    type="number" 
                                    min="0"
                                    value={reportData[teacher.teacher_id]?.total_attendance ?? ''}
                                    onChange={(e) => updateReportField(teacher.teacher_id, 'total_attendance', e.target.value)}
                                    className="h-7 w-12 text-center text-xs"
                                    placeholder="0"
                                  />
                                </div>
                              </div>

                              {/* Lesson Notes Row */}
                              <div className="flex items-center gap-2">
                                <label className="text-[10px] text-gray-500 shrink-0">Lesson Notes:</label>
                                <Select 
                                  value={reportData[teacher.teacher_id]?.lesson_notes_received || 'None'}
                                  onValueChange={(value) => updateReportField(teacher.teacher_id, 'lesson_notes_received', value)}
                                >
                                  <SelectTrigger className="h-7 w-28 text-xs">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="None">None</SelectItem>
                                    <SelectItem value="Some weeks">Some weeks</SelectItem>
                                    <SelectItem value="All weeks">All weeks</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </CardContent>
                          </Card>
                        ))}

                        {/* Teachers Work Output Verification Section */}
                        <div className="space-y-4 mt-6">
                          <div className="flex items-center justify-between border-b pb-2">
                            <h3 className="font-semibold text-sm flex items-center gap-2">
                              <BarChart3 className="h-4 w-4 text-purple-600" />
                              Teachers Work Output (Exercises)
                            </h3>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={loadTeachersWorkOutput}
                              disabled={loadingWorkOutput}
                              className="text-xs"
                            >
                              {loadingWorkOutput ? (
                                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              ) : (
                                <Download className="h-3 w-3 mr-1" />
                              )}
                              Load Submissions
                            </Button>
                          </div>

                          {teachersWorkOutput.length === 0 ? (
                            <div className="text-center py-4 text-gray-500 text-sm bg-gray-50 rounded-lg">
                              {loadingWorkOutput ? 'Loading...' : 'No work output submissions yet. Click "Load Submissions" to fetch.'}
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {teachersWorkOutput.map((teacher) => (
                                <Card key={teacher.teacher_id} className="border-l-4 border-l-purple-500">
                                  <CardContent className="p-3">
                                    <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                                      <Users className="h-4 w-4 text-purple-600" />
                                      <span className="font-semibold text-sm">{teacher.teacher_name}</span>
                                      <Badge variant="outline" className="text-[10px]">{teacher.teacher_email}</Badge>
                                    </div>

                                    {/* Work Output Table */}
                                    <div className="overflow-x-auto">
                                      <table className="w-full min-w-[800px] text-xs">
                                        <thead className="bg-gray-50">
                                          <tr>
                                            <th className="px-2 py-1 text-left font-semibold sticky left-0 bg-gray-50">Subject</th>
                                            {workOutputWeeks.map(week => (
                                              <th key={week} className="px-3 py-2 text-center font-semibold min-w-[85px]">W{week}</th>
                                            ))}
                                            <th className="px-2 py-1 text-center font-semibold bg-green-50 text-green-700">Verified</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                          {Object.keys(teacher.work_output || {}).map((subject) => (
                                            <tr key={subject}>
                                              <td className="px-2 py-1 font-medium sticky left-0 bg-white">{subject}</td>
                                              {workOutputWeeks.map(week => {
                                                const value = parseInt(teacher.work_output[subject]?.[week]) || 0;
                                                const verification = getVerification(teacher.teacher_id, subject, week);
                                                const hasValue = value > 0;
                                                
                                                return (
                                                  <td key={week} className="px-3 py-2">
                                                    {hasValue ? (
                                                      <div className="flex flex-col items-center gap-1.5">
                                                        <span className={`font-medium ${
                                                          verification?.status === 'compliant' ? 'text-green-600' :
                                                          verification?.status === 'non_compliant' ? 'text-red-600' : 'text-gray-700'
                                                        }`}>
                                                          {value}
                                                        </span>
                                                        <div className="flex gap-1.5">
                                                          <button
                                                            onClick={() => saveVerification(teacher.teacher_id, subject, week, 'compliant')}
                                                            className={`p-1 rounded ${verification?.status === 'compliant' ? 'bg-green-500 text-white' : 'bg-gray-200 hover:bg-green-200'}`}
                                                            title="Compliant"
                                                          >
                                                            <CheckCircle className="h-4 w-4" />
                                                          </button>
                                                          <button
                                                            onClick={() => {
                                                              const comment = prompt('Enter concern/comment for non-compliance:');
                                                              if (comment !== null) {
                                                                saveVerification(teacher.teacher_id, subject, week, 'non_compliant', comment);
                                                              }
                                                            }}
                                                            className={`p-1 rounded ${verification?.status === 'non_compliant' ? 'bg-red-500 text-white' : 'bg-gray-200 hover:bg-red-200'}`}
                                                            title={verification?.status === 'non_compliant' ? verification.comment : 'Non-Compliant'}
                                                          >
                                                            <XCircle className="h-4 w-4" />
                                                          </button>
                                                        </div>
                                                      </div>
                                                    ) : (
                                                      <span className="text-gray-300">-</span>
                                                    )}
                                                  </td>
                                                );
                                              })}
                                              <td className="px-2 py-1 text-center bg-green-50">
                                                <span className="font-bold text-green-700">
                                                  {calculateCompliantTotal(teacher.teacher_id, teacher.work_output, subject)}
                                                </span>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>

                                    <p className="text-[10px] text-gray-500 mt-2">
                                      Submitted: {teacher.submitted_at ? new Date(teacher.submitted_at).toLocaleString() : 'N/A'}
                                    </p>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Remarks Section for End of Term too */}
                        <div className="space-y-2">
                          <h3 className="font-semibold text-sm border-b pb-2">Headteacher's Remarks</h3>
                          <textarea
                            value={headteacherRemarks}
                            onChange={(e) => setHeadteacherRemarks(e.target.value)}
                            placeholder="Enter overall term summary, achievements, challenges..."
                            className="w-full min-h-[80px] p-3 border rounded-lg text-sm resize-y"
                          />
                        </div>
                      </div>
                    )}

                    {/* Save Button */}
                    <div className="flex justify-end pt-3 sm:pt-4">
                      <Button 
                        onClick={saveTermReport}
                        disabled={savingReport}
                        className={`w-full sm:w-auto sm:min-w-[150px] ${reportType === 'start_of_term' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
                      >
                        {savingReport ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save {reportType === 'start_of_term' ? 'Start of Term' : 'End of Term'} Report
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications" className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileEdit className="h-5 w-5 text-blue-600" />
                  Applications
                </CardTitle>
                <CardDescription>
                  Manage teacher applications and submit your own applications to SISO
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Toggle Buttons */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant={applicationView === 'teacher' ? 'default' : 'outline'}
                    onClick={() => { setApplicationView('teacher'); loadTeacherApplications(); }}
                    className={applicationView === 'teacher' ? 'bg-blue-600' : ''}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Teacher Applications
                    {newTeacherAppsCount > 0 && (
                      <Badge className="ml-2 bg-red-500 text-white text-xs">
                        {newTeacherAppsCount}
                      </Badge>
                    )}
                  </Button>
                  <Button
                    variant={applicationView === 'my' ? 'default' : 'outline'}
                    onClick={() => { setApplicationView('my'); loadMyApplications(); }}
                    className={applicationView === 'my' ? 'bg-green-600' : ''}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    My Applications
                  </Button>
                </div>

                {/* Teacher Applications View */}
                {applicationView === 'teacher' && (
                  <div className="space-y-4">
                    {loadingTeacherApps ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                      </div>
                    ) : teacherApplications.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p>No teacher applications received yet</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {teacherApplications.map((app) => (
                          <Card 
                            key={app.id} 
                            className={`border-l-4 cursor-pointer hover:shadow-md transition-shadow ${
                              app.status === 'approved' ? 'border-l-green-500 bg-green-50/30' :
                              app.status === 'disapproved' ? 'border-l-red-500 bg-red-50/30' :
                              app.status === 'escalated_to_siso' ? 'border-l-purple-500 bg-purple-50/30' :
                              'border-l-amber-500 bg-amber-50/30'
                            }`}
                            onClick={() => {
                              setSelectedApplication(app);
                              setShowApplicationPreview(true);
                              setPreviewActionMode(null);
                              setEscalateMemoText('');
                              setFormattedMemo('');
                              setShowMemoPreview(false);
                              setDisapproveComment('');
                            }}
                            data-testid={`application-card-${app.id}`}
                          >
                            <CardContent className="p-4">
                              <div className="flex flex-col sm:flex-row justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h4 className="font-semibold text-gray-900">{app.application_type}</h4>
                                    <Badge className={
                                      app.status === 'approved' ? 'bg-green-100 text-green-700' :
                                      app.status === 'disapproved' ? 'bg-red-100 text-red-700' :
                                      app.status === 'escalated_to_siso' ? 'bg-purple-100 text-purple-700' :
                                      'bg-amber-100 text-amber-700'
                                    }>
                                      {app.status === 'approved' && <><CheckCircle className="h-3 w-3 mr-1" />Approved</>}
                                      {app.status === 'disapproved' && <><XCircle className="h-3 w-3 mr-1" />Disapproved</>}
                                      {app.status === 'escalated_to_siso' && <><Forward className="h-3 w-3 mr-1" />Escalated</>}
                                      {app.status === 'pending' && <><Clock className="h-3 w-3 mr-1" />Pending</>}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-gray-600 mb-1">
                                    <span className="font-medium">From:</span> {app.teacher_name} ({app.teacher_email})
                                  </p>
                                  <p className="text-xs text-gray-500 mb-2">
                                    <span className="font-medium">Submitted:</span> {new Date(app.submitted_at).toLocaleString()}
                                  </p>
                                  
                                  {/* Action Details - Shows when action was taken */}
                                  {app.status !== 'pending' && app.action_at && (
                                    <div className={`mt-2 p-2 rounded text-xs ${
                                      app.status === 'approved' ? 'bg-green-50 border border-green-200' :
                                      app.status === 'disapproved' ? 'bg-red-50 border border-red-200' :
                                      'bg-purple-50 border border-purple-200'
                                    }`}>
                                      <p className="font-medium mb-1">
                                        {app.status === 'approved' && '✓ Approved'}
                                        {app.status === 'disapproved' && '✗ Disapproved'}
                                        {app.status === 'escalated_to_siso' && '↗ Escalated to SISO'}
                                        {' on '}{new Date(app.action_at).toLocaleString()}
                                      </p>
                                      {app.action_comment && (
                                        <p className="text-gray-600"><span className="font-medium">Reason:</span> {app.action_comment}</p>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* View Button */}
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedApplication(app);
                                      setShowApplicationPreview(true);
                                      setPreviewActionMode(null);
                                      setEscalateMemoText('');
                                      setFormattedMemo('');
                                      setShowMemoPreview(false);
                                      setDisapproveComment('');
                                    }}
                                  >
                                    <Eye className="h-4 w-4 mr-1" />
                                    {app.status === 'pending' ? 'View & Take Action' : 'View Application'}
                                  </Button>
                                  {app.status === 'pending' && (app.headteacher_memo || app.escalation_memo || app.memo_saved) && (
                                    <Button
                                      size="sm"
                                      className="bg-purple-600 hover:bg-purple-700 text-white"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                                          const res = await authenticatedFetch(`${backendUrl}/api/headteacher/application-action/${app.id}`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({
                                              action: 'submit_to_siso',
                                              comment: 'Submitted to SISO',
                                              formatted_memo: app.headteacher_memo || app.escalation_memo
                                            })
                                          });
                                          if (res.ok) {
                                            toast.success('Application submitted to SISO');
                                            loadTeacherApplications();
                                          } else {
                                            const err = await res.json();
                                            toast.error(err.detail || 'Failed to submit');
                                          }
                                        } catch (err) { toast.error('Error submitting'); }
                                      }}
                                      data-testid={`submit-to-siso-${app.id}`}
                                    >
                                      <Forward className="h-4 w-4 mr-1" />
                                      Submit to SISO
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* My Applications View */}
                {applicationView === 'my' && (
                  <div className="space-y-6">
                    {/* Application Form */}
                    <div className="space-y-4 border-b pb-6">
                      <h3 className="font-semibold text-gray-900">Create New Application</h3>
                      
                      {/* Application Type Select */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Application Type</label>
                        <Select value={applicationType} onValueChange={setApplicationType}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select application type..." />
                          </SelectTrigger>
                          <SelectContent>
                            {applicationTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Plain Text Editor */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Write your application content (plain text)
                        </label>
                        <Textarea
                          value={applicationPlainText}
                          onChange={(e) => setApplicationPlainText(e.target.value)}
                          placeholder="Write the main content of your application here. The AI will format it into a professional official letter..."
                          className="min-h-[150px] resize-y"
                          data-testid="application-text-input"
                        />
                        <p className="text-xs text-gray-500">
                          Tip: Include the reason, dates, and any relevant details. The AI will add proper formatting and formal language.
                        </p>
                      </div>

                      {/* Generate Button */}
                      <div className="flex justify-end">
                        <Button
                          onClick={generateApplicationLetter}
                          disabled={generatingLetter || !applicationType || !applicationPlainText.trim()}
                          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                          data-testid="generate-letter-btn"
                        >
                          {generatingLetter ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              Generate Letter
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Generated Letter - Tiptap Editor */}
                    {showLetterPreview && generatedLetter && (
                      <div className="space-y-4 border-t pt-6">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            <FileText className="h-5 w-5 text-green-600" />
                            {letterSaved ? 'Application Letter (Saved)' : 'Edit Application Letter'}
                          </h3>
                          {letterSaved && (
                            <Badge className="bg-green-100 text-green-700">Saved</Badge>
                          )}
                        </div>
                        
                        {/* Tiptap A4 Editor */}
                        {!letterSaved ? (
                          <div className="space-y-3">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <p className="text-sm text-blue-700 flex items-center gap-2">
                                <FileEdit className="h-4 w-4" />
                                Review and edit the letter below before saving. Your edits will be preserved in the final PDF.
                              </p>
                            </div>
                            <div className="bg-white shadow-lg rounded-lg overflow-hidden border-2 border-gray-200 mx-auto" style={{ maxWidth: '210mm' }}>
                              <RichTextEditor
                                content={editedLetterHtml}
                                onChange={setEditedLetterHtml}
                                placeholder="Edit your application letter here..."
                                minHeight="500px"
                                className="[&_.ProseMirror]:px-6 [&_.ProseMirror]:sm:px-10 [&_.ProseMirror]:py-8 [&_.ProseMirror]:font-serif [&_.ProseMirror]:text-base [&_.ProseMirror]:leading-7"
                              />
                            </div>
                            <div className="flex flex-col sm:flex-row justify-end gap-2">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setShowLetterPreview(false);
                                  setGeneratedLetter('');
                                  setEditedLetterHtml('');
                                  setLetterSaved(false);
                                  setApplicationDraftId(null);
                                }}
                                className="text-red-600 border-red-300 hover:bg-red-50 w-full sm:w-auto"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Discard
                              </Button>
                              <Button
                                onClick={async () => {
                                  setSavingLetter(true);
                                  try {
                                    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                                    const response = await authenticatedFetch(`${backendUrl}/api/headteacher/save-application-draft`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      credentials: 'include',
                                      body: JSON.stringify({
                                        html_content: editedLetterHtml,
                                        application_type: applicationTypes.find(t => t.value === applicationType)?.label || applicationType,
                                        plain_text: applicationPlainText,
                                        formatted_letter: generatedLetter,
                                      })
                                    });
                                    if (response.ok) {
                                      const data = await response.json();
                                      setApplicationDraftId(data.draft_id);
                                      setLetterSaved(true);
                                      toast.success('Application letter saved and PDF generated!');
                                    } else {
                                      const err = await response.json();
                                      throw new Error(err.detail || 'Failed to save draft');
                                    }
                                  } catch (error) {
                                    console.error('Error saving draft:', error);
                                    toast.error(error.message || 'Failed to save letter');
                                  } finally {
                                    setSavingLetter(false);
                                  }
                                }}
                                disabled={savingLetter}
                                className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                                data-testid="save-letter-btn"
                              >
                                {savingLetter ? (
                                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                                ) : (
                                  <><CheckCircle className="h-4 w-4 mr-2" />Save Letter</>
                                )}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* After Save: Show formatted preview + edit option */
                          <div className="space-y-3">
                            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 sm:p-6 shadow-inner max-h-[400px] overflow-y-auto mx-auto" style={{ maxWidth: '210mm' }}>
                              <div className="font-['Georgia',_'Times_New_Roman',_serif] text-base leading-7 text-gray-900" dangerouslySetInnerHTML={{ __html: editedLetterHtml }} />
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setLetterSaved(false)}
                                className="w-full sm:w-auto"
                              >
                                <FileEdit className="h-4 w-4 mr-1" />
                                Edit Again
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={printLetter}
                                className="text-gray-600 w-full sm:w-auto"
                              >
                                <Printer className="h-4 w-4 mr-1" />
                                Print/PDF
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Attachments Section - visible after letter is saved */}
                        {letterSaved && (
                          <div className="border-t pt-4 mt-4">
                            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                              <h4 className="font-medium text-gray-700 flex items-center gap-2">
                                <Paperclip className="h-4 w-4" />
                                Supporting Documents
                                {applicationAttachments.length > 0 && (
                                  <Badge className="bg-blue-100 text-blue-700">{applicationAttachments.length}</Badge>
                                )}
                              </h4>
                              <div>
                                <input
                                  type="file"
                                  ref={attachmentInputRef}
                                  onChange={handleAttachmentUpload}
                                  multiple
                                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                                  className="hidden"
                                  id="ht-attachment-input"
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => attachmentInputRef.current?.click()}
                                  disabled={uploadingAttachment}
                                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                >
                                  {uploadingAttachment ? (
                                    <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Uploading...</>
                                  ) : (
                                    <><PlusIcon className="h-4 w-4 mr-1" />Add Document</>
                                  )}
                                </Button>
                              </div>
                            </div>
                            
                            {applicationAttachments.length === 0 ? (
                              <p className="text-sm text-gray-500 italic">No attachments yet. Add supporting documents if needed.</p>
                            ) : (
                              <div className="space-y-2">
                                {applicationAttachments.map((att) => (
                                  <div key={att.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <FileIcon className="h-4 w-4 text-blue-500 shrink-0" />
                                      <span className="text-sm font-medium truncate">{att.filename}</span>
                                      <span className="text-xs text-gray-400 shrink-0">{formatFileSize(att.file_size)}</span>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeAttachment(att.id)}
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0 shrink-0"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <p className="text-xs text-gray-400 mt-2">Allowed: PDF, Images (PNG, JPEG), Word, Excel. Max 5MB each.</p>
                          </div>
                        )}

                        {/* Submit Button - only after letter is saved */}
                        {letterSaved && (
                          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setShowLetterPreview(false);
                                setGeneratedLetter('');
                                setEditedLetterHtml('');
                                setLetterSaved(false);
                                setApplicationDraftId(null);
                              }}
                              className="text-red-600 border-red-300 hover:bg-red-50 w-full sm:w-auto"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Discard
                            </Button>
                            <Button
                              onClick={submitMyApplication}
                              disabled={submittingApplication}
                              className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                              data-testid="submit-application-btn"
                            >
                              {submittingApplication ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
                              ) : (
                                <><Send className="h-4 w-4 mr-2" />Submit to SISO</>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Previous Applications */}
                    {myApplications.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Clock className="h-5 w-5 text-gray-500" />
                          My Submitted Applications
                        </h3>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {myApplications.map((app) => (
                            <div 
                              key={app.id} 
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-gray-900">{app.application_type}</p>
                                <p className="text-xs text-gray-500">
                                  Submitted: {new Date(app.submitted_at).toLocaleString()}
                                </p>
                              </div>
                              <Badge 
                                className={
                                  app.status === 'approved' ? 'bg-green-100 text-green-700' :
                                  app.status === 'disapproved' ? 'bg-red-100 text-red-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }
                              >
                                {app.status === 'approved' ? (
                                  <><CheckCircle className="h-3 w-3 mr-1" />Approved</>
                                ) : app.status === 'disapproved' ? (
                                  <><AlertCircle className="h-3 w-3 mr-1" />Disapproved</>
                                ) : (
                                  <><Clock className="h-3 w-3 mr-1" />Pending</>
                                )}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  My Profile & Settings
                </CardTitle>
                <CardDescription>
                  Manage your profile picture, digital signature, stamp, and contact details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {profileLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <>
                    {/* Upload Sections - Inline on Desktop */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Profile Picture */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-blue-600" />
                          Profile Picture
                        </h3>
                        <div className="flex flex-col items-center gap-3">
                          {userProfile?.profile_picture ? (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${userProfile.profile_picture}`}
                              alt="Profile"
                              className="w-20 h-20 rounded-full object-cover border-2 border-blue-200"
                              data-testid="profile-picture-preview"
                            />
                          ) : (
                            <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                              <Users className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                          <div className="flex flex-col gap-2 w-full">
                            <label className="cursor-pointer w-full">
                              <input type="file" accept="image/*" className="hidden" onChange={handleProfilePictureUpload} disabled={uploadingProfilePic} data-testid="profile-picture-input" />
                              <Button variant="outline" size="sm" disabled={uploadingProfilePic} className="w-full" asChild>
                                <span>{uploadingProfilePic ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Uploading</> : <><Upload className="h-3 w-3 mr-1" />Upload</>}</span>
                              </Button>
                            </label>
                            {userProfile?.profile_picture && (
                              <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50 w-full" onClick={() => handleDeleteUpload('profile_picture')}>
                                <Trash2 className="h-3 w-3 mr-1" />Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Digital Signature */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                          <FileEdit className="h-4 w-4 text-green-600" />
                          Digital Signature
                        </h3>
                        <div className="flex flex-col items-center gap-3">
                          {userProfile?.digital_signature ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${userProfile.digital_signature}`} alt="Signature" className="w-28 h-14 object-contain border rounded bg-white p-1" data-testid="signature-preview" />
                          ) : (
                            <div className="w-28 h-14 bg-gray-100 flex items-center justify-center border rounded">
                              <span className="text-xs text-gray-400">No signature</span>
                            </div>
                          )}
                          <div className="flex flex-col gap-2 w-full">
                            <label className="cursor-pointer w-full">
                              <input type="file" accept="image/*" className="hidden" onChange={handleSignatureUpload} disabled={uploadingSignature} data-testid="signature-input" />
                              <Button variant="outline" size="sm" disabled={uploadingSignature} className="w-full" asChild>
                                <span>{uploadingSignature ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Uploading</> : <><Upload className="h-3 w-3 mr-1" />Upload</>}</span>
                              </Button>
                            </label>
                            {userProfile?.digital_signature && (
                              <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50 w-full" onClick={() => handleDeleteUpload('signature')}>
                                <Trash2 className="h-3 w-3 mr-1" />Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Official Stamp */}
                      <div className="border rounded-lg p-4 bg-amber-50">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                          <Stamp className="h-4 w-4 text-amber-600" />
                          Official Stamp
                        </h3>
                        <div className="flex flex-col items-center gap-3">
                          {userProfile?.digital_stamp ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${userProfile.digital_stamp}`} alt="Stamp" className="w-16 h-16 object-contain border rounded bg-white p-1" data-testid="stamp-preview" />
                          ) : (
                            <div className="w-16 h-16 bg-white flex items-center justify-center border rounded">
                              <span className="text-xs text-gray-400">No stamp</span>
                            </div>
                          )}
                          <div className="flex flex-col gap-2 w-full">
                            <label className="cursor-pointer w-full">
                              <input type="file" accept="image/*" className="hidden" onChange={handleStampUpload} disabled={uploadingStamp} data-testid="stamp-input" />
                              <Button variant="outline" size="sm" disabled={uploadingStamp} className="w-full" asChild>
                                <span>{uploadingStamp ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Uploading</> : <><Upload className="h-3 w-3 mr-1" />Upload</>}</span>
                              </Button>
                            </label>
                            {userProfile?.digital_stamp && (
                              <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50 w-full" onClick={() => handleDeleteUpload('stamp')}>
                                <Trash2 className="h-3 w-3 mr-1" />Remove
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 text-center">For memos to SISO</p>
                      </div>
                    </div>

                    {/* Contact Details Section */}
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-purple-600" />
                        Contact Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Town/City</label>
                          <input type="text" value={profileFormData.town} onChange={(e) => setProfileFormData(prev => ({ ...prev, town: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your town" data-testid="profile-town-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                          <input type="tel" value={profileFormData.phone} onChange={(e) => setProfileFormData(prev => ({ ...prev, phone: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., 0244123456" data-testid="profile-phone-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">District File Number</label>
                          <input type="text" value={profileFormData.file_number} onChange={(e) => setProfileFormData(prev => ({ ...prev, file_number: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your district file number" data-testid="profile-file-number-input" />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                          <input type="text" value={profileFormData.school_name || ''} onChange={(e) => setProfileFormData(prev => ({ ...prev, school_name: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your school name" data-testid="profile-school-name-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">District Name</label>
                          <input type="text" value={profileFormData.district_name || ''} onChange={(e) => setProfileFormData(prev => ({ ...prev, district_name: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your district name" data-testid="profile-district-name-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Region Name</label>
                          <input type="text" value={profileFormData.region_name || ''} onChange={(e) => setProfileFormData(prev => ({ ...prev, region_name: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your region name" data-testid="profile-region-name-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Regional File Number</label>
                          <input type="text" value={profileFormData.regional_file_number || ''} onChange={(e) => setProfileFormData(prev => ({ ...prev, regional_file_number: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your regional file number" data-testid="profile-regional-file-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Staff ID</label>
                          <input type="text" value={profileFormData.staff_id || ''} onChange={(e) => setProfileFormData(prev => ({ ...prev, staff_id: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your GES Staff ID" data-testid="profile-staff-id-input" />
                        </div>
                      </div>
                      
                      <div className="mt-4 flex justify-end">
                        <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-blue-600 hover:bg-blue-700" data-testid="save-profile-btn">
                          {savingProfile ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><CheckCircle className="h-4 w-4 mr-2" />Save Details</>}
                        </Button>
                      </div>
                    </div>

                    {/* Account Info */}
                    {userProfile && (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <h3 className="font-semibold text-gray-900 mb-3">Account Information</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div><span className="text-gray-500">Name:</span></div>
                          <div className="font-medium">{userProfile.name}</div>
                          <div><span className="text-gray-500">Email:</span></div>
                          <div className="font-medium">{userProfile.email}</div>
                          <div><span className="text-gray-500">Role:</span></div>
                          <div className="font-medium capitalize">{userProfile.role}</div>
                          {userProfile.school && (
                            <>
                              <div><span className="text-gray-500">School:</span></div>
                              <div className="font-medium">{userProfile.school}</div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promotion Exams Preparation Tab */}
          <TabsContent value="promotion_exams">
            <PromotionPreparation />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-amber-600" />
              Add Comment to Lesson Note
            </DialogTitle>
            <DialogDescription>
              {currentCommentSubmission && (
                <span className="text-xs">
                  {currentCommentSubmission.subject} - Week {currentCommentSubmission.week}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Textarea
              placeholder="Enter your comment for this lesson note..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              className="min-h-[120px]"
            />
            <p className="text-xs text-gray-500">
              This comment will be visible to the teacher on their dashboard.
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCommentDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={saveComment}
              disabled={savingComment}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {savingComment ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Comment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application Preview Modal - Full Screen View with Actions */}
      <Dialog open={showApplicationPreview} onOpenChange={(open) => {
        setShowApplicationPreview(open);
        if (!open) {
          setSelectedApplication(null);
          setPreviewActionMode(null);
          setEscalateMemoText('');
          setFormattedMemo('');
          setShowMemoPreview(false);
          setDisapproveComment('');
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader className="flex-shrink-0 space-y-1">
            <DialogTitle className="flex flex-wrap items-center gap-2 text-base sm:text-lg">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
              <span>Teacher Application</span>
              {selectedApplication && (
                <Badge className={`text-xs ${
                  selectedApplication.status === 'approved' ? 'bg-green-100 text-green-700' :
                  selectedApplication.status === 'disapproved' ? 'bg-red-100 text-red-700' :
                  selectedApplication.status === 'escalated_to_siso' ? 'bg-purple-100 text-purple-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {selectedApplication.status === 'approved' && 'Approved'}
                  {selectedApplication.status === 'disapproved' && 'Disapproved'}
                  {selectedApplication.status === 'escalated_to_siso' && 'Escalated to SISO'}
                  {selectedApplication.status === 'pending' && 'Pending Review'}
                </Badge>
              )}
            </DialogTitle>
            {selectedApplication && (
              <DialogDescription className="text-xs sm:text-sm">
                <span className="font-medium">{selectedApplication.application_type}</span> from {selectedApplication.teacher_name}
                <span className="text-gray-400 block sm:inline sm:ml-2">Submitted {new Date(selectedApplication.submitted_at).toLocaleString()}</span>
              </DialogDescription>
            )}
          </DialogHeader>
          
          {selectedApplication && (
            <div className="space-y-4">
              {/* Application Letter Preview */}
              <div className="bg-gray-50 rounded-lg border max-h-[40vh] sm:max-h-[50vh] overflow-y-auto">
                <div className="bg-white p-4 sm:p-8 shadow-sm mx-auto max-w-[21cm]" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                  <div className="prose prose-sm max-w-none">
                    {selectedApplication.html_content ? (
                      <div dangerouslySetInnerHTML={{ __html: selectedApplication.html_content }} />
                    ) : (
                      renderFormattedLetter(
                        selectedApplication.formatted_letter,
                        selectedApplication.signature_url ? `${process.env.REACT_APP_BACKEND_URL}${selectedApplication.signature_url}` : null,
                        null
                      )
                    )}
                  </div>
                </div>
              </div>
              
              {/* Attachments Section - Display teacher's attachments */}
              {selectedApplication.attachments && selectedApplication.attachments.length > 0 && (
                <div className="border-t pt-3 sm:pt-4">
                  <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-2 sm:mb-3 text-sm sm:text-base">
                    <Paperclip className="h-4 w-4" />
                    Attached Documents
                    <Badge className="bg-blue-100 text-blue-700">{selectedApplication.attachments.length}</Badge>
                  </h4>
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                    {selectedApplication.attachments.map((att) => (
                      <button
                        key={att.id}
                        onClick={async () => {
                          const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                          try {
                            const response = await authenticatedFetch(`${backendUrl}/api/application/attachment/${att.id}`);
                            if (response.ok) {
                              const blob = await response.blob();
                              const url = URL.createObjectURL(blob);
                              window.open(url, '_blank');
                              setTimeout(() => URL.revokeObjectURL(url), 60000);
                            } else { toast.error('Failed to open attachment'); }
                          } catch (e) { toast.error('Error opening attachment'); }
                        }}
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border hover:bg-blue-50 hover:border-blue-300 transition-colors cursor-pointer"
                      >
                        <FileIcon className="h-4 w-4 text-blue-500" />
                        <span className="text-sm font-medium">{att.filename}</span>
                        <Download className="h-3 w-3 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action Panel - Only for pending applications */}
              {selectedApplication.status === 'pending' && (
                <div className="border-t pt-3 sm:pt-4 mt-2 sm:mt-4">
                  {/* Action Mode Selection */}
                  {!previewActionMode && (
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-center">
                      <Button
                        className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                        onClick={() => setPreviewActionMode('approve')}
                      >
                        <ThumbsUp className="h-4 w-4 mr-2" />
                        Approve Application
                      </Button>
                      <Button
                        variant="destructive"
                        className="w-full sm:w-auto"
                        onClick={() => setPreviewActionMode('disapprove')}
                      >
                        <ThumbsDown className="h-4 w-4 mr-2" />
                        Disapprove Application
                      </Button>
                      <Button
                        variant="outline"
                        className="border-purple-500 text-purple-600 hover:bg-purple-50 w-full sm:w-auto"
                        onClick={() => setPreviewActionMode('escalate')}
                      >
                        <Forward className="h-4 w-4 mr-2" />
                        Escalate to SISO
                      </Button>
                    </div>
                  )}
                  
                  {/* Approve Confirmation */}
                  {previewActionMode === 'approve' && (
                    <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-green-700 font-medium flex items-center gap-2 text-sm sm:text-base">
                        <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
                        Confirm Approval
                      </p>
                      <p className="text-xs sm:text-sm text-green-600">
                        You are about to approve this application from {selectedApplication.teacher_name}. 
                        The teacher will be notified of your decision.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <Button variant="outline" onClick={() => setPreviewActionMode(null)} className="w-full sm:w-auto">
                          Cancel
                        </Button>
                        <Button
                          className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                          onClick={async () => {
                            await processApplicationAction(selectedApplication.id, 'approve', '');
                            setShowApplicationPreview(false);
                            setSelectedApplication(null);
                          }}
                          disabled={processingAction === selectedApplication.id}
                        >
                          {processingAction === selectedApplication.id ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <ThumbsUp className="h-4 w-4 mr-2" />
                          )}
                          Confirm Approval
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Disapprove with Reason */}
                  {previewActionMode === 'disapprove' && (
                    <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-red-700 font-medium flex items-center gap-2 text-sm sm:text-base">
                        <XCircle className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
                        Disapprove Application
                      </p>
                      <div className="space-y-2">
                        <label className="text-xs sm:text-sm font-medium text-red-700">Reason for disapproval (required):</label>
                        <Textarea
                          placeholder="Enter the reason for disapproving this application..."
                          value={disapproveComment}
                          onChange={(e) => setDisapproveComment(e.target.value)}
                          className="min-h-[80px] resize-none border-red-200 focus:border-red-500 text-sm"
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <Button variant="outline" className="w-full sm:w-auto" onClick={() => {
                          setPreviewActionMode(null);
                          setDisapproveComment('');
                        }}>
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          className="w-full sm:w-auto"
                          onClick={async () => {
                            await processApplicationAction(selectedApplication.id, 'disapprove', disapproveComment);
                            setShowApplicationPreview(false);
                            setSelectedApplication(null);
                            setDisapproveComment('');
                          }}
                          disabled={processingAction === selectedApplication.id || !disapproveComment.trim()}
                        >
                          {processingAction === selectedApplication.id ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <ThumbsDown className="h-4 w-4 mr-2" />
                          )}
                          Confirm Disapproval
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Escalate to SISO with Memo */}
                  {previewActionMode === 'escalate' && (
                    <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-purple-700 font-medium flex items-center gap-2 text-sm sm:text-base">
                        <Forward className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
                        Escalate to SISO - Create Forwarding Memo
                      </p>
                      <p className="text-xs text-purple-600">
                        Your memo will be attached as a separate document alongside the teacher's application (unaltered).
                      </p>
                      
                      {!showMemoPreview ? (
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-purple-700">Write your memo content:</label>
                            <Textarea
                              placeholder="Write your recommendations/comments for the SISO regarding this application..."
                              value={escalateMemoText}
                              onChange={(e) => setEscalateMemoText(e.target.value)}
                              className="min-h-[100px] resize-none border-purple-200 focus:border-purple-500 bg-white"
                            />
                          </div>
                          <div className="flex gap-3">
                            <Button variant="outline" onClick={() => {
                              setPreviewActionMode(null);
                              setEscalateMemoText('');
                            }}>
                              Cancel
                            </Button>
                            <Button
                              className="bg-purple-600 hover:bg-purple-700"
                              onClick={() => generateEscalationMemo(selectedApplication.teacher_name, selectedApplication.application_type)}
                              disabled={generatingMemo || !escalateMemoText.trim()}
                            >
                              {generatingMemo ? (
                                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating...</>
                              ) : (
                                <><Sparkles className="h-4 w-4 mr-2" />Generate Memo</>
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : !memoSaved ? (
                        /* Tiptap Editor for memo editing */
                        <div className="space-y-3">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-700 flex items-center gap-2">
                              <FileEdit className="h-4 w-4" />
                              Review and edit the memo below before saving. Your edits will be preserved in the final PDF.
                            </p>
                          </div>
                          <div className="bg-white shadow-lg rounded-lg overflow-hidden border-2 border-purple-200">
                            <RichTextEditor
                              content={editedMemoHtml}
                              onChange={setEditedMemoHtml}
                              placeholder="Edit your memo here..."
                              minHeight="250px"
                              className="[&_.ProseMirror]:px-4 [&_.ProseMirror]:sm:px-10 [&_.ProseMirror]:py-6 [&_.ProseMirror]:sm:py-8 [&_.ProseMirror]:font-serif [&_.ProseMirror]:text-sm [&_.ProseMirror]:sm:text-base [&_.ProseMirror]:leading-6 [&_.ProseMirror]:sm:leading-7"
                            />
                          </div>
                          <div className="flex flex-wrap gap-2 pt-2">
                            <Button variant="outline" size="sm" onClick={() => {
                              setShowMemoPreview(false);
                              setFormattedMemo('');
                              setEditedMemoHtml('');
                            }}>
                              ← Back
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => generateEscalationMemo(selectedApplication.teacher_name, selectedApplication.application_type)}
                              disabled={generatingMemo}
                            >
                              {generatingMemo ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" />Regenerate</>}
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={async () => {
                                setSavingMemo(true);
                                try {
                                  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                                  const res = await authenticatedFetch(`${backendUrl}/api/headteacher/save-memo-draft`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ application_id: selectedApplication.id, html_content: editedMemoHtml })
                                  });
                                  if (res.ok) {
                                    setMemoSaved(true);
                                    toast.success('Memo saved and PDF generated!');
                                    setTeacherApplications(prev => prev.map(a =>
                                      a.id === selectedApplication.id ? { ...a, headteacher_memo: editedMemoHtml, memo_saved: true } : a
                                    ));
                                  } else {
                                    const err = await res.json();
                                    throw new Error(err.detail || 'Failed to save memo');
                                  }
                                } catch (e) { toast.error(e.message || 'Failed to save memo'); }
                                finally { setSavingMemo(false); }
                              }}
                              disabled={savingMemo}
                              data-testid="save-memo-btn"
                            >
                              {savingMemo ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><CheckCircle className="h-4 w-4 mr-2" />Save Memo</>}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* After Save: Read-only preview + Submit to SISO */
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Badge className="bg-green-100 text-green-700">Memo Saved</Badge>
                            <Button variant="outline" size="sm" onClick={printMemo} className="text-xs">
                              <Printer className="h-3 w-3 mr-1" />Print/PDF
                            </Button>
                          </div>
                          <div className="bg-white border-2 border-purple-300 rounded-lg p-4 max-h-[250px] overflow-y-auto
                            font-['Georgia',_'Times_New_Roman',_serif] text-sm leading-relaxed text-gray-900"
                            data-testid="memo-preview"
                            dangerouslySetInnerHTML={{ __html: editedMemoHtml }}
                          />
                          <div className="flex flex-wrap gap-2 pt-2 border-t border-purple-200">
                            <Button variant="outline" size="sm" onClick={() => setMemoSaved(false)}>
                              <FileEdit className="h-4 w-4 mr-1" />Edit Again
                            </Button>
                            <Button
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                              onClick={async () => {
                                await processApplicationAction(selectedApplication.id, 'submit_to_siso', escalateMemoText, editedMemoHtml);
                                cancelEscalation();
                                setShowApplicationPreview(false);
                                setSelectedApplication(null);
                              }}
                              disabled={processingAction === selectedApplication.id}
                              data-testid="submit-to-siso-btn"
                            >
                              {processingAction === selectedApplication.id ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
                              ) : (
                                <><Send className="h-4 w-4 mr-2" />Submit to SISO</>
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Show action details for processed applications */}
              {selectedApplication.status !== 'pending' && selectedApplication.action_at && (
                <div className={`flex-shrink-0 border-t pt-4 mt-4 p-4 rounded-lg ${
                  selectedApplication.status === 'approved' ? 'bg-green-50 border border-green-200' :
                  selectedApplication.status === 'disapproved' ? 'bg-red-50 border border-red-200' :
                  'bg-purple-50 border border-purple-200'
                }`}>
                  <p className="font-medium mb-1">
                    {selectedApplication.status === 'approved' && '✓ Approved'}
                    {selectedApplication.status === 'disapproved' && '✗ Disapproved'}
                    {selectedApplication.status === 'escalated_to_siso' && '↗ Escalated to SISO'}
                    {' on '}{new Date(selectedApplication.action_at).toLocaleString()}
                  </p>
                  {selectedApplication.action_comment && (
                    <p className="text-gray-600 text-sm"><span className="font-medium">Reason:</span> {selectedApplication.action_comment}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HeadteacherDashboard;